import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {FileUploadProvider, useFileUploadStore} from '@common/uploads/uploader/file-upload-provider';
import {useDroppable} from '@common/ui/interactions/dnd/use-droppable';
import {FileTypeIcon} from '@common/uploads/file-type-icon/file-type-icon';
import {prettyBytes} from '@common/uploads/utils/pretty-bytes';
import {CloseIcon} from '@common/icons/material/Close';
import {RefreshIcon} from '@common/icons/material/Refresh';
import {FileDownloadIcon} from '@common/icons/material/FileDownload';
import {PrintIcon} from '@common/icons/material/Print';
import {ContentCopyIcon} from '@common/icons/material/ContentCopy';
import {DeleteOutlineIcon} from '@common/icons/material/DeleteOutline';
import {HistoryIcon} from '@common/icons/material/History';
import {SiteHeader} from '@common/ui/navigation/site-header';
import clsx from 'clsx';
import {isEmail} from '@common/utils/string/is-email';
import {apiClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';
import {useActiveWorkspaceId} from '@common/workspace/active-workspace-id-context';
import {toast} from '@common/ui/toast/toast';
import {
  clearGuestSendDraft,
  getGuestSendDraftEmail,
  hasGuestDraftForEmail,
  markGuestEmailVerified,
  setGuestSendDraft,
} from './guest-send-storage';
import {
  confirmGuestSend,
  createGuestSendDraft,
  initiateGuestSend,
  resendGuestSendOtp,
  setupGuestSendUploadAuth,
} from './guest-send-api';
import {GuestSendOtpModal} from './guest-send-otp-modal';
import {createSendPackage, type SendPackageMode} from './send-package-api';
import {prepareSendDelivery} from './send-e2ee-prepare';
import type {FileEntry} from '@common/uploads/file-entry';
import {UploadedFile} from '@common/uploads/uploaded-file';
import {SendQrDisplay} from './send-qr-display';
import {
  downloadQrPng,
  downloadTextFile,
  printReceiveLink,
} from './send-qr-utils';
import {useExpiryCountdown} from './use-expiry-countdown';
import {useSendTransferLimit} from './use-send-transfer-limit';
import {TransferAddonCheckoutDialog} from './transfer-addon-checkout-dialog';
import {
  transferAddonSkuFromLabel,
  type TransferAddonSku,
} from './transfer-addon-api';
import useClipboard from 'react-use-clipboard';

// Figma node 329:6 (add email icon), downloaded to local public/images.
const FIGMA_PLUS_ICON = '/images/figma-plus-icon.svg';
// Figma node 325:9 (upload icon), downloaded to local public/images.
const FIGMA_UPLOAD_ICON = '/images/figma-upload-icon.svg';
// Figma node 286:151 (toggle knob), downloaded to local public/images.
const CT_FAVICON = '/favicon.png';
const FRAME_WIDTH = 1897;
const FRAME_HEIGHT = 876;
// Site header + desktop canvas bottom padding (lg:pt-[17px] + ~64px nav + pb-[24px]).
const SEND_PAGE_DESKTOP_CHROME_PX = 105;
// Figma desktop form column: inputs/messages are 411px wide; message is 138px tall.
const DESKTOP_FORM_FIELD_CLASS = 'w-[411px]';
const DESKTOP_MESSAGE_FIELD_CLASS = 'h-[138px] w-[411px]';
// Match left-column control spacing (transfer limit / expiration use ~20px).
const DESKTOP_FORM_FIELD_STACK_GAP = 'mt-[20px]';
type ExpirationUnit = 'Minutes' | 'Hours' | 'Days' | 'Months' | 'Years';
const EXPIRATION_UNITS = ['Minutes', 'Hours', 'Days', 'Months', 'Years'] as const;
const EXPIRATION_MAX_COUNTS: Record<ExpirationUnit, number> = {
  Minutes: 45,
  Hours: 18,
  Days: 20,
  Months: 11,
  Years: 5,
};

interface RecipientPill {
  id: string;
  email: string;
  hasCloudTransferAccount: boolean;
}

interface NormalizedUserResult {
  name?: string;
  description?: string;
}

interface NormalizedUsersResponse {
  results?: NormalizedUserResult[];
}

type DrivePickerLocation =
  | 'All files'
  | 'Tags'
  | 'Favorites'
  | 'Shared'
  | 'Recent';

type OwnerFilterOption = {
  label: string;
  email: string;
};

type HeaderTimeFilter =
  | {kind: 'all'; label: string}
  | {kind: 'today'; label: string}
  | {kind: 'pastWeek'; label: string}
  | {kind: 'pastMonth'; label: string}
  | {kind: 'year'; year: number; label: string};

interface DriveApiEntry {
  id: number;
  name: string;
  type?: string;
  extension?: string;
  mime?: string;
  url?: string;
  hash?: string;
  path?: string;
  file_name?: string;
  file_size?: number;
  parent_id?: number | null;
  updated_at?: string;
  users?: Array<{
    id?: number;
    email?: string;
    display_name?: string;
    owns_entry?: boolean;
  }>;
  tags?: Array<{name?: string}>;
  encryption_mode?: string;
  e2ee_iv?: string | null;
  e2ee_wrapped_key?: string | null;
}

interface DriveEntriesApiResponse {
  data?: DriveApiEntry[];
  current_page?: number;
  last_page?: number;
  pagination?: {
    data?: DriveApiEntry[];
    current_page?: number;
    last_page?: number;
  };
}

function formatDriveDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = date.getMonth() + 1;
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
}

function parseDriveDateLabel(value?: string): Date | null {
  if (!value) return null;
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = 2000 + Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isSameCalendarDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatDriveSize(fileSize?: number): string {
  if (!fileSize || fileSize <= 0) return '';
  return prettyBytes(fileSize, {compact: true, precision: 1}).replace('kB', 'KB');
}

function mapDriveEntryToFileType(entry: DriveApiEntry): string {
  if (entry.type === 'folder') return 'folder';
  const raw = (entry.extension || entry.type || entry.mime || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif', 'image'].includes(raw)) return 'image';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'video'].includes(raw)) return 'video';
  if (['mp3', 'wav', 'aac', 'ogg', 'flac', 'audio'].includes(raw)) return 'audio';
  if (raw === 'pdf') return 'pdf';
  if (['zip', 'rar', '7z', 'tar', 'gz', 'archive'].includes(raw)) return 'archive';
  if (['doc', 'docx', 'word'].includes(raw)) return 'word';
  if (['ppt', 'pptx', 'powerpoint'].includes(raw)) return 'powerPoint';
  if (['xls', 'xlsx', 'csv', 'spreadsheet'].includes(raw)) return 'spreadsheet';
  if (['txt', 'md', 'markdown', 'json', 'xml', 'html', 'css', 'js', 'ts', 'tsx', 'jsx', 'text'].includes(raw)) return 'text';
  if (entry.mime?.startsWith('image/')) return 'image';
  if (entry.mime?.startsWith('video/')) return 'video';
  if (entry.mime?.startsWith('audio/')) return 'audio';
  return 'default';
}

function getDriveEntryOwner(
  entry: DriveApiEntry,
  currentUserEmail?: string | null,
): string {
  const normalizedCurrentUserEmail = (currentUserEmail ?? '')
    .trim()
    .toLowerCase();
  const matchingUser = normalizedCurrentUserEmail
    ? entry.users?.find(
        user =>
          (user.email ?? '').trim().toLowerCase() === normalizedCurrentUserEmail,
      )
    : null;
  if (matchingUser) return 'You';

  const ownerUser =
    entry.users?.find(user => user.owns_entry) || entry.users?.[0];
  return ownerUser?.display_name || ownerUser?.email || 'You';
}

function getDriveTagColor(tagName?: string): string {
  const tagColorByKey: Record<string, string> = {
    important: '#4fc3dc',
    school: '#ed5555',
    work: '#56d665',
    focus: '#d7df4d',
    testing: '#7f52d9',
    archive: '#9f6ff2',
    other: '#8b8b8b',
  };

  return tagColorByKey[tagName?.trim().toLowerCase() || ''] ?? '#8b8b8b';
}

function filterDriveEntriesForLocation(
  rows: DriveApiEntry[],
  location: DrivePickerLocation,
  currentUserEmail?: string | null,
): DriveApiEntry[] {
  const normalizedCurrentUserEmail = (currentUserEmail ?? '')
    .trim()
    .toLowerCase();

  if (location === 'Tags') {
    return rows.filter(row => (row.tags?.length ?? 0) > 0);
  }

  if (location === 'Shared') {
    return rows.filter(row =>
      (row.users ?? []).some(user => {
        const email = (user.email ?? '').trim().toLowerCase();
        return !!email && email !== normalizedCurrentUserEmail;
      }),
    );
  }

  return rows;
}

function mapDriveEntryToFileEntry(entry: DriveApiEntry): FileEntry {
  return {
    id: entry.id,
    name: entry.name,
    mime: entry.mime || '',
    url: entry.url || '',
    hash: entry.hash || String(entry.id),
    extension: entry.extension,
    type: entry.type || '',
    path: entry.path || '',
    file_name: entry.file_name || entry.name,
    file_size: entry.file_size,
    parent_id: entry.parent_id ?? null,
    updated_at: entry.updated_at,
    users: (entry.users || [])
      .filter(user => !!user.id && !!user.email)
      .map(user => ({id: user.id!, email: user.email!})),
    encryption_mode: entry.encryption_mode,
    e2ee_iv: entry.e2ee_iv,
    e2ee_wrapped_key: entry.e2ee_wrapped_key,
  };
}

export function SendPage() {
  return (
    <FileUploadProvider>
      <SendPageContent />
    </FileUploadProvider>
  );
}

function SendPageContent() {
  const {user, isLoggedIn} = useAuth();
  const {workspaceId} = useActiveWorkspaceId();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [lastReceiveUrl, setLastReceiveUrl] = useState('');
  const [lastExpiresAt, setLastExpiresAt] = useState<string | null>(null);
  // Figma frame is "Add password ON" for node 286:96.
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false);
  const [passwordPhrase, setPasswordPhrase] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderEmailError, setSenderEmailError] = useState('');
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [pendingSendId, setPendingSendId] = useState<number | null>(null);
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
  const [isOtpResending, setIsOtpResending] = useState(false);
  const [sharedRecipients, setSharedRecipients] = useState<RecipientPill[]>([]);
  const [isShareBoxVisible, setIsShareBoxVisible] = useState(false);
  const [isExpirationModalOpen, setIsExpirationModalOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [isCountDropdownOpen, setIsCountDropdownOpen] = useState(false);
  const [expirationUnit, setExpirationUnit] = useState<ExpirationUnit | null>(null);
  const [expirationCount, setExpirationCount] = useState<number | null>(null);
  const [isTimeBoxSelected, setIsTimeBoxSelected] = useState(false);
  const [isCountBoxSelected, setIsCountBoxSelected] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showInvalidEmailPopup, setShowInvalidEmailPopup] = useState(false);
  const [isDropzoneDragOver, setIsDropzoneDragOver] = useState(false);
  const [isPageFileDragActive, setIsPageFileDragActive] = useState(false);
  const [isAddFilesMenuOpen, setIsAddFilesMenuOpen] = useState(false);
  const [activeMode, setActiveMode] = useState<'share' | 'qr' | 'link'>('share');
  const [isQrGenerated, setIsQrGenerated] = useState(false);
  const [isLinkGenerated, setIsLinkGenerated] = useState(false);
  const [isTransferLimitMenuOpen, setIsTransferLimitMenuOpen] = useState(false);
  const [transferCheckoutDialog, setTransferCheckoutDialog] = useState<{
    open: boolean;
    price: string;
    limit: string;
    sku: TransferAddonSku;
  }>({open: false, price: '$2.99', limit: '+20GB', sku: '20gb'});

  const [isDrivePickerPopupOpen, setIsDrivePickerPopupOpen] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [drivePickerLocation, setDrivePickerLocation] =
    useState<DrivePickerLocation>('All files');
  const [drivePickerSearch, setDrivePickerSearch] = useState('');
  const [drivePickerRows, setDrivePickerRows] = useState<DriveApiEntry[]>([]);
  const [isDrivePickerLoading, setIsDrivePickerLoading] = useState(false);
  const [drivePickerIndexedTypeOptions, setDrivePickerIndexedTypeOptions] =
    useState<string[]>([]);
  const [selectedDriveEntries, setSelectedDriveEntries] = useState<FileEntry[]>([]);
  const [drivePickerFolderStack, setDrivePickerFolderStack] = useState<
    Array<{id: string; name: string}>
  >([]);
  const [isDrivePickerTypeMenuOpen, setIsDrivePickerTypeMenuOpen] =
    useState(false);
  const [isDrivePickerOwnerMenuOpen, setIsDrivePickerOwnerMenuOpen] =
    useState(false);
  const [isDrivePickerTimeMenuOpen, setIsDrivePickerTimeMenuOpen] =
    useState(false);
  const [drivePickerTypeFilter, setDrivePickerTypeFilter] = useState<string | null>(
    null,
  );
  const [drivePickerOwnerFilter, setDrivePickerOwnerFilter] = useState<string | null>(
    null,
  );
  const [drivePickerTimeFilter, setDrivePickerTimeFilter] =
    useState<HeaderTimeFilter>({
      kind: 'all',
      label: 'Time',
    });
  const [showGuestLockedFieldsNotice, setShowGuestLockedFieldsNotice] =
    useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? FRAME_WIDTH : window.innerWidth,
  );
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 1080 : window.innerHeight,
  );
  const popupTimeoutRef = useRef<number | null>(null);
  const guestNoticeTimeoutRef = useRef<number | null>(null);
  const expirationNoticeTimeoutRef = useRef<number | null>(null);
  const transferLimitMobileRef = useRef<HTMLDivElement | null>(null);
  const transferLimitDesktopRef = useRef<HTMLDivElement | null>(null);
  const shareBoxMobileRef = useRef<HTMLDivElement | null>(null);
  const shareBoxDesktopRef = useRef<HTMLDivElement | null>(null);
  const expirationMobileRef = useRef<HTMLDivElement | null>(null);
  const expirationDesktopRef = useRef<HTMLDivElement | null>(null);
  const passwordMobileRef = useRef<HTMLDivElement | null>(null);
  const passwordDesktopRef = useRef<HTMLDivElement | null>(null);
  const fileDragDepthRef = useRef(0);
  const maxMessageLength = 300;

  const fileUploads = useFileUploadStore(s => s.fileUploads);
  const uploadMultiple = useFileUploadStore(s => s.uploadMultiple);
  const uploadSingle = useFileUploadStore(s => s.uploadSingle);
  const abortUpload = useFileUploadStore(s => s.abortUpload);
  const removeUpload = useFileUploadStore(s => s.removeUpload);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const uploadBoxMobileRef = useRef<HTMLElement | null>(null);
  const uploadBoxDesktopRef = useRef<HTMLElement | null>(null);
  const guestDraftPromiseRef = useRef<Promise<boolean> | null>(null);
  const [, copyReceiveUrl] = useClipboard(lastReceiveUrl, {successDuration: 1500});
  const expiryCountdown = useExpiryCountdown(
    (isQrGenerated || isLinkGenerated) && lastExpiresAt ? lastExpiresAt : null,
  );
  const {label: transferLimitLabel} = useSendTransferLimit(
    isLoggedIn,
    senderEmail,
  );

  const uploadsArray = useMemo(() => Array.from(fileUploads.values()), [fileUploads]);
  const combinedSendEntries = useMemo(() => {
    const uploadedEntries = uploadsArray
      .filter(upload => upload.status === 'completed' && upload.entry?.id)
      .map(upload => upload.entry as FileEntry);
    const entryMap = new Map<number, FileEntry>();
    [...uploadedEntries, ...selectedDriveEntries].forEach(entry => {
      entryMap.set(entry.id, entry);
    });
    return Array.from(entryMap.values());
  }, [selectedDriveEntries, uploadsArray]);
  const activeDrivePickerFolderId =
    drivePickerFolderStack[drivePickerFolderStack.length - 1]?.id ?? '0';
  const activeDrivePickerFolderLabel =
    drivePickerFolderStack[drivePickerFolderStack.length - 1]?.name ??
    'My Drive';
  const drivePickerTypeOptions = useMemo(() => {
    if (drivePickerIndexedTypeOptions.length) {
      return drivePickerIndexedTypeOptions;
    }
    const types = new Set<string>();
    drivePickerRows.forEach(row => {
      const type = (row.extension || row.type || '').trim();
      if (type) {
        types.add(type === 'folder' ? 'folder' : type);
      }
    });
    return Array.from(types).sort((left, right) => left.localeCompare(right));
  }, [drivePickerIndexedTypeOptions, drivePickerRows]);
  const drivePickerOwnerOptions = useMemo(() => {
    const currentUserEmail = (user?.email ?? '').trim().toLowerCase();
    const owners = new Map<string, OwnerFilterOption>();
    drivePickerRows.forEach(row => {
      (row.users || []).forEach(owner => {
        const email = (owner.email ?? '').trim().toLowerCase();
        if (!email || owners.has(email)) return;
        owners.set(email, {
          email,
          label:
            email === currentUserEmail
              ? 'You'
              : owner.display_name?.trim() || owner.email?.trim() || email,
        });
      });
    });
    return Array.from(owners.values()).sort((left, right) =>
      left.label.localeCompare(right.label),
    );
  }, [drivePickerRows, user?.email]);
  const drivePickerYearOptions = useMemo(() => {
    const years = new Set<number>();
    drivePickerRows.forEach(row => {
      const date = row.updated_at ? new Date(row.updated_at) : null;
      if (date && !Number.isNaN(date.getTime())) {
        years.add(date.getFullYear());
      }
    });
    return Array.from(years).sort((left, right) => right - left);
  }, [drivePickerRows]);
  const filteredDrivePickerRows = useMemo(() => {
    return drivePickerRows.filter(row => {
      const rowType = (row.extension || row.type || '').trim().toLowerCase();
      if (
        drivePickerTypeFilter &&
        rowType !== drivePickerTypeFilter.trim().toLowerCase()
      ) {
        return false;
      }

      if (drivePickerOwnerFilter) {
        const normalizedOwnerFilter = drivePickerOwnerFilter.trim().toLowerCase();
        const hasOwner = (row.users || []).some(owner => {
          const email = (owner.email ?? '').trim().toLowerCase();
          const label = (owner.display_name ?? owner.email ?? '')
            .trim()
            .toLowerCase();
          return (
            email === normalizedOwnerFilter || label === normalizedOwnerFilter
          );
        });
        if (!hasOwner) return false;
      }

      if (drivePickerTimeFilter.kind !== 'all') {
        const modifiedDate = parseDriveDateLabel(formatDriveDate(row.updated_at));
        if (!modifiedDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (drivePickerTimeFilter.kind === 'today') {
          if (!isSameCalendarDay(modifiedDate, today)) return false;
        } else if (drivePickerTimeFilter.kind === 'pastWeek') {
          const start = new Date(today);
          start.setDate(start.getDate() - 7);
          if (modifiedDate < start) return false;
        } else if (drivePickerTimeFilter.kind === 'pastMonth') {
          const start = new Date(today);
          start.setMonth(start.getMonth() - 1);
          if (modifiedDate < start) return false;
        } else if (drivePickerTimeFilter.kind === 'year') {
          if (modifiedDate.getFullYear() !== drivePickerTimeFilter.year) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    drivePickerOwnerFilter,
    drivePickerRows,
    drivePickerTimeFilter,
    drivePickerTypeFilter,
  ]);
  const hasSelectedExpiration = expirationUnit !== null && expirationCount !== null;

  /** Link / QR sends require a password (password-protected sharing). */
  const passwordLockedForLinkQr = Boolean(
    user && (activeMode === 'link' || activeMode === 'qr'),
  );

  useEffect(() => {
    setupGuestSendUploadAuth();
  }, []);

  useEffect(() => {
    if (!isLoggedIn && activeMode !== 'share') {
      setActiveMode('share');
    }
  }, [activeMode, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      clearGuestSendDraft();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (passwordLockedForLinkQr) {
      setIsPasswordEnabled(true);
    }
  }, [passwordLockedForLinkQr]);

  useEffect(() => {
    if (isLoggedIn) return;
    setIsPasswordEnabled(false);
    setPasswordPhrase('');
    setIsExpirationModalOpen(false);
    setMessage('');
  }, [isLoggedIn]);

  const totalPercentage = useMemo(() => {
    if (!uploadsArray.length) return 0;
    const total = uploadsArray.reduce((acc, upload) => acc + upload.percentage, 0);
    return total / uploadsArray.length;
  }, [uploadsArray]);
  const failedUploads = useMemo(
    () => uploadsArray.filter(upload => upload.status === 'failed'),
    [uploadsArray],
  );
  const hasUploadError = failedUploads.length > 0;
  const failedUploadPreview = useMemo(() => {
    const names = failedUploads.map(upload => upload.file.name);
    if (!names.length) return '';
    const preview = names.slice(0, 2).join(', ');
    return names.length > 2 ? `${preview} +${names.length - 2} more` : preview;
  }, [failedUploads]);

  const fetchAllDriveEntries = async (
    extraParams: Partial<{
      query: string;
      starredOnly: boolean;
      recentOnly: boolean;
      folderId: string | number;
    }>,
  ): Promise<DriveApiEntry[]> => {
    const allRows: DriveApiEntry[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await apiClient.get<DriveEntriesApiResponse>(
        'drive/file-entries',
        {
          params: {
            page,
            orderBy: 'updated_at',
            orderDir: 'desc',
            workspaceId,
            ...extraParams,
          },
        },
      );
      const data = response.data;
      const apiRows = data?.data ?? data?.pagination?.data ?? [];
      allRows.push(...apiRows);
      const currentPage =
        data?.current_page ?? data?.pagination?.current_page ?? page;
      const lastPage = data?.last_page ?? data?.pagination?.last_page ?? page;
      hasMore = currentPage < lastPage && apiRows.length > 0;
      page += 1;
    }

    return allRows;
  };

  useEffect(() => {
    if (!isDrivePickerPopupOpen || !isLoggedIn) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setIsDrivePickerLoading(true);
      try {
        const query = drivePickerSearch.trim();
        let rows = await fetchAllDriveEntries(
          drivePickerLocation === 'Favorites'
            ? {query, starredOnly: true}
            : drivePickerLocation === 'Recent'
              ? {query, recentOnly: true}
              : drivePickerLocation === 'All files'
                ? {query, folderId: activeDrivePickerFolderId}
                : {query},
        );

        rows = filterDriveEntriesForLocation(
          rows,
          drivePickerLocation,
          user?.email,
        );

        if (!cancelled) {
          setDrivePickerRows(rows);
        }
      } catch {
        if (!cancelled) {
          setDrivePickerRows([]);
        }
      } finally {
        if (!cancelled) {
          setIsDrivePickerLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    drivePickerLocation,
    drivePickerSearch,
    activeDrivePickerFolderId,
    isDrivePickerPopupOpen,
    isLoggedIn,
    user?.email,
    workspaceId,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadIndexedTypeFilterOptions = async () => {
      try {
        const response = await apiClient.get<{types?: string[]}>(
          'drive/file-entry-types',
          {
            params: {workspaceId},
          },
        );
        if (cancelled) return;
        setDrivePickerIndexedTypeOptions(response.data.types ?? []);
      } catch {
        if (cancelled) return;
        setDrivePickerIndexedTypeOptions([]);
      }
    };

    void loadIndexedTypeFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const openDrivePicker = () => {
    setIsAddFilesMenuOpen(false);
    setDrivePickerSearch('');
    setDrivePickerLocation('All files');
    setDrivePickerFolderStack([]);
    setDrivePickerTypeFilter(null);
    setDrivePickerOwnerFilter(null);
    setDrivePickerTimeFilter({kind: 'all', label: 'Time'});
    setIsDrivePickerTypeMenuOpen(false);
    setIsDrivePickerOwnerMenuOpen(false);
    setIsDrivePickerTimeMenuOpen(false);
    setIsLocationMenuOpen(false);
    setIsDrivePickerPopupOpen(true);
  };

  const addDriveEntryToSendList = (entry: DriveApiEntry) => {
    const fileEntry = mapDriveEntryToFileEntry(entry);
    setSelectedDriveEntries(current =>
      current.some(item => item.id === fileEntry.id)
        ? current
        : [...current, fileEntry],
    );
    setIsDrivePickerPopupOpen(false);
  };

  const removeDriveEntryFromSendList = (entryId: number) => {
    setSelectedDriveEntries(current =>
      current.filter(entry => entry.id !== entryId),
    );
  };

  const openDriveFolder = (entry: DriveApiEntry) => {
    const folderId = entry.hash || String(entry.id);
    setDrivePickerLocation('All files');
    setDrivePickerFolderStack(current =>
      drivePickerLocation === 'All files'
        ? [...current, {id: folderId, name: entry.name}]
        : [{id: folderId, name: entry.name}],
    );
  };

  const goBackDriveFolder = () => {
    setDrivePickerFolderStack(current => current.slice(0, -1));
  };

  const {droppableProps} = useDroppable({
    id: 'send-page-dropzone',
    ref: dropRef,
    types: ['nativeFile'],
    onDragEnter: () => {
      setIsDropzoneDragOver(true);
    },
    onDragLeave: () => {
      setIsDropzoneDragOver(false);
    },
    onDrop: async draggable => {
      fileDragDepthRef.current = 0;
      setIsPageFileDragActive(false);
      setIsDropzoneDragOver(false);
      if (!(await ensureGuestDraftForUpload())) return;
      const files = await draggable.getData();
      uploadMultiple(files);
    },
  });

  const ensureGuestDraftForUpload = async (): Promise<boolean> => {
    if (isLoggedIn) return true;
    const email = senderEmail.trim();
    if (!email) {
      setSenderEmailError('Please enter your sender email address.');
      return false;
    }
    if (!isEmail(email)) {
      setSenderEmailError('Please enter a valid sender email address.');
      return false;
    }
    setSenderEmailError('');
    return ensureGuestDraft(email);
  };

  const ensureGuestDraft = async (email: string): Promise<boolean> => {
    const normalized = email.toLowerCase().trim();
    if (hasGuestDraftForEmail(normalized)) {
      return true;
    }

    if (guestDraftPromiseRef.current) {
      return guestDraftPromiseRef.current;
    }

    if (
      getGuestSendDraftEmail() &&
      getGuestSendDraftEmail() !== normalized
    ) {
      clearGuestSendDraft();
    }

    const promise = (async () => {
      try {
        const data = await createGuestSendDraft(normalized);
        setGuestSendDraft(data.draftToken, data.senderEmail);
        return true;
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          'Could not start guest session. Please try again.';
        setSenderEmailError(msg);
        return false;
      }
    })();

    guestDraftPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      if (guestDraftPromiseRef.current === promise) {
        guestDraftPromiseRef.current = null;
      }
    }
  };

  const handleSenderEmailBlur = () => {
    const email = senderEmail.trim();
    if (!email) return;
    if (!isEmail(email)) {
      setSenderEmailError('Please enter a valid sender email address.');
      return;
    }
    setSenderEmailError('');
    const normalized = email.toLowerCase();
    if (getGuestSendDraftEmail() && getGuestSendDraftEmail() !== normalized) {
      clearGuestSendDraft();
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      if (!(await ensureGuestDraftForUpload())) {
        e.target.value = '';
        return;
      }
      uploadMultiple(e.target.files);
    }
  };

  const resolveCloudTransferAccount = async (email: string) => {
    try {
      const response = await Promise.race([
        apiClient.get<NormalizedUsersResponse>('normalized-models/user', {
          params: {query: email, perPage: 8},
          timeout: 5000,
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 5000),
        ),
      ]);
      const queryEmail = email.toLowerCase();
      const users = response.data.results || [];
      return users.some(user => {
        const userEmail = (user.description || user.name || '').toLowerCase();
        return userEmail === queryEmail;
      });
    } catch {
      return false;
    }
  };

  const addRecipientFromInput = () => {
    const raw = emailInput.trim();
    if (!raw) return;

    const parts = raw.split(/[,;\n]+/).map(p => p.trim()).filter(Boolean);
    if (!parts.length) return;

    const invalid = parts.find(p => p && !isEmail(p.trim()));
    if (invalid) {
      setEmailError('Please enter a valid email address');
      triggerInvalidEmailPopup();
      return;
    }

    setSharedRecipients(prev => {
      let next = [...prev];
      for (const part of parts) {
        const trimmed = part.trim().replace(/,+$/, '');
        if (!trimmed || !isEmail(trimmed)) continue;
        const normalized = trimmed.toLowerCase();
        if (!isLoggedIn && next.length >= 3) break;
        if (next.some(p => p.email.toLowerCase() === normalized)) continue;
        const pillId = `${normalized}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        next.push({
          id: pillId,
          email: trimmed,
          hasCloudTransferAccount: false,
        });
        void resolveCloudTransferAccount(trimmed).then(hasCloudTransferAccount => {
          setSharedRecipients(current =>
            current.map(pill =>
              pill.id === pillId ? {...pill, hasCloudTransferAccount} : pill,
            ),
          );
        });
      }
      if (!isLoggedIn && parts.length > 0 && next.length >= 3 && parts.length + prev.length > 3) {
        setEmailError('Guest send allows at most 3 recipients.');
      } else {
        setEmailError('');
      }
      return next;
    });
    setEmailInput('');
    if (!isShareBoxVisible) {
      setIsShareBoxVisible(true);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      setIsShareBoxVisible(true);
      addRecipientFromInput();
    }
  };

  const handleAddRecipientClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (emailInput.trim()) {
      setIsShareBoxVisible(true);
      addRecipientFromInput();
      return;
    }
    setIsShareBoxVisible(prev => !prev);
  };

  const triggerInvalidEmailPopup = () => {
    setShowInvalidEmailPopup(true);
    if (popupTimeoutRef.current) {
      window.clearTimeout(popupTimeoutRef.current);
    }
    popupTimeoutRef.current = window.setTimeout(() => {
      setShowInvalidEmailPopup(false);
    }, 1800);
  };

  const resetLinkQrResult = () => {
    setIsQrGenerated(false);
    setIsLinkGenerated(false);
    setLastReceiveUrl('');
    setLastExpiresAt(null);
  };

  const resetFormAfterSend = () => {
    toast.positive('Files sent successfully.');
    clearGuestSendDraft();
    setSendError('');
    resetLinkQrResult();
    setEmailInput('');
    setSharedRecipients([]);
    setSubject('');
    setMessage('');
    setIsShareBoxVisible(false);
    setOtpCode('');
    setPendingSendId(null);

    [...fileUploads.values()].forEach(upload => {
      if (upload.status === 'inProgress') {
        abortUpload(upload.file.id);
      }
      removeUpload(upload.file.id);
    });
  };

  const handleGuestVerifyAndSend = async () => {
    if (!pendingSendId || otpCode.trim().length !== 6) return;
    setIsOtpSubmitting(true);
    setOtpError('');
    try {
      const data = await confirmGuestSend({
        pendingSendId,
        code: otpCode.trim(),
      });
      markGuestEmailVerified(senderEmail);
      setIsOtpModalOpen(false);
      setOtpCode('');
      setPendingSendId(null);
      resetFormAfterSend();
    } catch (error: any) {
      setOtpError(
        error?.response?.data?.message ||
          'Verification failed. Please try again.',
      );
    } finally {
      setIsOtpSubmitting(false);
    }
  };

  const handleGuestResendOtp = async () => {
    if (!pendingSendId) return;
    setIsOtpResending(true);
    setOtpError('');
    try {
      await resendGuestSendOtp(pendingSendId);
    } catch (error: any) {
      setOtpError(error?.response?.data?.message || 'Could not resend code.');
    } finally {
      setIsOtpResending(false);
    }
  };

  const runGuestSend = async (
    completedEntryIds: number[],
    recipients: string[],
  ) => {
    const email = senderEmail.trim();
    if (!isEmail(email)) {
      setSenderEmailError('Please enter your sender email address.');
      return;
    }
    if (!(await ensureGuestDraft(email))) return;

    setIsSending(true);
    setSendError('');
    setOtpError('');

    try {
      const data = await initiateGuestSend({
        entryIds: completedEntryIds,
        recipients,
        subject: subject || null,
        message: message || null,
      });

      if (data.otpRequired === false) {
        markGuestEmailVerified(email);
        resetFormAfterSend();
        return;
      }

      setPendingSendId(data.pendingSendId ?? null);
      setOtpCode('');
      setIsOtpModalOpen(true);
    } catch (error: any) {
      setSendError(
        error?.response?.data?.message ||
          'Could not start send. Please try again.',
      );
    } finally {
      setIsSending(false);
    }
  };

  const getCompletedEntryIds = () =>
    Array.from(new Set(combinedSendEntries.map(entry => entry.id)));

  const validateLoggedInSend = (
    mode: SendPackageMode,
    completedEntryIds: number[],
    recipients: string[],
  ): string | null => {
    if (!completedEntryIds.length) {
      return 'Please upload at least one file successfully before sending.';
    }
    if (mode === 'share' && recipients.length === 0) {
      return 'Please add at least one recipient email.';
    }
    if ((mode === 'link' || mode === 'qr') && !hasSelectedExpiration) {
      showExpirationRequiredMessage();
      return 'Please select an expiration before generating.';
    }
    if ((mode === 'link' || mode === 'qr') && !passwordPhrase.trim()) {
      return 'Please set a password for link or QR send.';
    }
    return null;
  };

  const submitLoggedInSend = async (mode: SendPackageMode) => {
    const candidate = emailInput.trim().replace(/,+$/, '');
    if (candidate && !isEmail(candidate)) {
      setEmailError('Please enter a valid email address');
      triggerInvalidEmailPopup();
      return;
    }
    setEmailError('');
    if (candidate) addRecipientFromInput();

    const completedEntryIds = getCompletedEntryIds();
    const recipients = sharedRecipients.map(item => item.email);
    const validationError = validateLoggedInSend(mode, completedEntryIds, recipients);
    if (validationError) {
      if (validationError !== 'Please select an expiration before generating.') {
        setSendError(validationError);
      }
      return;
    }

    setIsSending(true);
    setSendError('');
    try {
      const entries = combinedSendEntries.filter(entry =>
        completedEntryIds.includes(entry.id),
      );

      const uploadPlainFile = (file: File): Promise<number> =>
        new Promise((resolve, reject) => {
          uploadSingle(new UploadedFile(file), {
            onSuccess: entry => resolve(entry.id),
            onError: message => reject(new Error(message || 'Upload failed')),
          });
        });

      const delivery = await prepareSendDelivery({
        mode,
        entryIds: completedEntryIds,
        recipients: mode === 'share' ? recipients : [],
        entries,
        uploadPlainFile,
      });

      const data = await createSendPackage({
        mode,
        entryIds: delivery.entryIds,
        recipients: mode === 'share' ? recipients : [],
        subject: subject || null,
        message: message || null,
        expiresMinutes: expirationToMinutes(),
        password:
          isPasswordEnabled || passwordLockedForLinkQr
            ? passwordPhrase || null
            : null,
        plainCopies: delivery.plainCopies.length
          ? delivery.plainCopies
          : undefined,
        e2eeDeliveries: delivery.e2eeDeliveries.length
          ? delivery.e2eeDeliveries
          : undefined,
      });

      if (mode === 'share') {
        resetFormAfterSend();
        return;
      }

      setLastReceiveUrl(data.receiveUrl);
      setLastExpiresAt(data.sendPackage?.expires_at ?? null);
      if (mode === 'qr') {
        setIsQrGenerated(true);
        toast.positive('QR code created.');
      } else {
        setIsLinkGenerated(true);
        toast.positive('Link created.');
      }
    } catch (error: any) {
      setSendError(
        error?.response?.data?.message || 'Could not send files. Please try again.',
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSendClick = async () => {
    const completedEntryIds = getCompletedEntryIds();
    const recipients = sharedRecipients.map(item => item.email);

    if (!isLoggedIn) {
      const candidate = emailInput.trim().replace(/,+$/, '');
      if (candidate && !isEmail(candidate)) {
        setEmailError('Please enter a valid email address');
        triggerInvalidEmailPopup();
        return;
      }
      setEmailError('');
      if (candidate) addRecipientFromInput();
      if (!completedEntryIds.length) {
        setSendError('Please upload at least one file successfully before sending.');
        return;
      }
      if (recipients.length === 0) {
        setSendError('Please add at least one recipient email.');
        return;
      }
      await runGuestSend(completedEntryIds, recipients);
      return;
    }

    await submitLoggedInSend('share');
  };

  const handleCopyReceiveUrl = () => {
    if (!lastReceiveUrl) {
      toast.danger('Generate a link or QR code first.');
      return;
    }
    copyReceiveUrl();
    toast.positive('Link copied to clipboard.');
  };

  const handleDownloadQr = async () => {
    if (!lastReceiveUrl) return;
    try {
      await downloadQrPng(lastReceiveUrl);
      toast.positive('QR code downloaded.');
    } catch {
      toast.danger('Could not download QR code.');
    }
  };

  const handlePrintLinkOrQr = () => {
    if (!lastReceiveUrl) return;
    printReceiveLink(
      activeMode === 'qr' ? 'CloudTransfer Send QR' : 'CloudTransfer Send Link',
      lastReceiveUrl,
    );
  };

  const handleDownloadLink = () => {
    if (!lastReceiveUrl) return;
    downloadTextFile(lastReceiveUrl, 'cloudtransfer-send-link.txt');
    toast.positive('Link saved as text file.');
  };

  const renderSenderEmailField = (variant: 'mobile' | 'desktop' = 'mobile') => {
    if (isLoggedIn || activeMode !== 'share') return null;

    return (
      <div className={variant === 'desktop' ? DESKTOP_FORM_FIELD_STACK_GAP : 'mt-[10px]'}>
        <input
          type="email"
          value={senderEmail}
          onChange={e => {
            const value = e.target.value;
            setSenderEmail(value);
            if (senderEmailError) setSenderEmailError('');
            const stored = getGuestSendDraftEmail();
            if (stored && value.trim().toLowerCase() !== stored) {
              clearGuestSendDraft();
            }
          }}
          onBlur={handleSenderEmailBlur}
          autoComplete="email"
          className="h-[65px] w-full rounded-[24px] border-2 border-transparent bg-alt px-[20px] text-[17px] text-main placeholder:text-muted outline-none focus:border-primary"
          placeholder="Your email (sender)"
        />
        {senderEmailError && (
          <p className="mt-[6px] text-[13px] font-medium text-[#E34444]">{senderEmailError}</p>
        )}
      </div>
    );
  };

  const expirationToMinutes = () => {
    if (!expirationUnit || !expirationCount) return undefined;
    switch (expirationUnit) {
      case 'Minutes':
        return expirationCount;
      case 'Hours':
        return expirationCount * 60;
      case 'Days':
        return expirationCount * 24 * 60;
      case 'Months':
        return expirationCount * 30 * 24 * 60;
      case 'Years':
        return expirationCount * 365 * 24 * 60;
      default:
        return undefined;
    }
  };

  const handleTransferLimitItemClick = (limit: string, price: string) => {
    setIsTransferLimitMenuOpen(false);
    const sku = transferAddonSkuFromLabel(limit);
    if (!isLoggedIn && !isEmail(senderEmail.trim())) {
      toast.danger('Enter your email first to buy more transfer.');
      return;
    }
    setTransferCheckoutDialog({
      open: true,
      price,
      limit,
      sku,
    });
  };

  const showGuestNotice = () => {
    setShowGuestLockedFieldsNotice(true);
    if (guestNoticeTimeoutRef.current) {
      window.clearTimeout(guestNoticeTimeoutRef.current);
    }
    guestNoticeTimeoutRef.current = window.setTimeout(() => {
      setShowGuestLockedFieldsNotice(false);
    }, 5000);
  };

  const handleGuestLockedControl = (
    e: React.MouseEvent,
    onAllowed?: () => void,
  ) => {
    if (!isLoggedIn) {
      e.preventDefault();
      showGuestNotice();
      return;
    }
    onAllowed?.();
  };

  const [showExpirationRequiredNotice, setShowExpirationRequiredNotice] =
    useState(false);

  const showExpirationRequiredMessage = () => {
    setShowExpirationRequiredNotice(true);
    if (expirationNoticeTimeoutRef.current) {
      window.clearTimeout(expirationNoticeTimeoutRef.current);
    }
    expirationNoticeTimeoutRef.current = window.setTimeout(() => {
      setShowExpirationRequiredNotice(false);
    }, 5000);
  };

  const handleGenerateQr = () => {
    void submitLoggedInSend('qr');
  };

  const handleGenerateLink = () => {
    void submitLoggedInSend('link');
  };

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

    const onWindowDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      fileDragDepthRef.current += 1;
      setIsPageFileDragActive(true);
    };

    const onWindowDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      if (fileDragDepthRef.current <= 0) return;
    };

    const onWindowDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (fileDragDepthRef.current === 0 || !e.relatedTarget) {
        setIsPageFileDragActive(false);
        setIsDropzoneDragOver(false);
      }
    };

    const onWindowDrop = () => {
      fileDragDepthRef.current = 0;
      setIsPageFileDragActive(false);
      setIsDropzoneDragOver(false);
    };
    const onWindowDragEnd = () => {
      fileDragDepthRef.current = 0;
      setIsPageFileDragActive(false);
      setIsDropzoneDragOver(false);
    };
    const forceClearDragState = () => {
      fileDragDepthRef.current = 0;
      setIsPageFileDragActive(false);
      setIsDropzoneDragOver(false);
    };

    window.addEventListener('dragenter', onWindowDragEnter);
    window.addEventListener('dragover', onWindowDragOver);
    window.addEventListener('dragleave', onWindowDragLeave);
    window.addEventListener('drop', onWindowDrop);
    window.addEventListener('dragend', onWindowDragEnd);
    window.addEventListener('mouseup', forceClearDragState);
    window.addEventListener('blur', forceClearDragState);
    document.addEventListener('visibilitychange', forceClearDragState);

    return () => {
      window.removeEventListener('dragenter', onWindowDragEnter);
      window.removeEventListener('dragover', onWindowDragOver);
      window.removeEventListener('dragleave', onWindowDragLeave);
      window.removeEventListener('drop', onWindowDrop);
      window.removeEventListener('dragend', onWindowDragEnd);
      window.removeEventListener('mouseup', forceClearDragState);
      window.removeEventListener('blur', forceClearDragState);
      document.removeEventListener('visibilitychange', forceClearDragState);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        window.clearTimeout(popupTimeoutRef.current);
      }
      if (guestNoticeTimeoutRef.current) {
        window.clearTimeout(guestNoticeTimeoutRef.current);
      }
      if (expirationNoticeTimeoutRef.current) {
        window.clearTimeout(expirationNoticeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hasSelectedExpiration && showExpirationRequiredNotice) {
      setShowExpirationRequiredNotice(false);
    }
  }, [hasSelectedExpiration, showExpirationRequiredNotice]);

  useEffect(() => {
    if (!showGuestLockedFieldsNotice) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const clickedTrigger = !!target?.closest('[data-guest-lock-trigger]');
      const clickedPopup = !!target?.closest('[data-guest-lock-popup]');
      if (!clickedTrigger && !clickedPopup) {
        setShowGuestLockedFieldsNotice(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [showGuestLockedFieldsNotice]);

  useEffect(() => {
    if (!isTransferLimitMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inMobile = !!(transferLimitMobileRef.current && target && transferLimitMobileRef.current.contains(target));
      const inDesktop = !!(transferLimitDesktopRef.current && target && transferLimitDesktopRef.current.contains(target));
      if (!inMobile && !inDesktop) {
        setIsTransferLimitMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [isTransferLimitMenuOpen]);

  useEffect(() => {
    if (!isAddFilesMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inMobile =
        !!(uploadBoxMobileRef.current &&
          target &&
          uploadBoxMobileRef.current.contains(target));
      const inDesktop =
        !!(uploadBoxDesktopRef.current &&
          target &&
          uploadBoxDesktopRef.current.contains(target));
      if (!inMobile && !inDesktop) {
        setIsAddFilesMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [isAddFilesMenuOpen]);

  useEffect(() => {
    if (!isShareBoxVisible) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inMobile =
        !!(shareBoxMobileRef.current &&
          target &&
          shareBoxMobileRef.current.contains(target));
      const inDesktop =
        !!(shareBoxDesktopRef.current &&
          target &&
          shareBoxDesktopRef.current.contains(target));
      if (!inMobile && !inDesktop) {
        setIsShareBoxVisible(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [isShareBoxVisible]);

  useEffect(() => {
    if (!isExpirationModalOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const inMobile =
        !!(expirationMobileRef.current &&
          target &&
          expirationMobileRef.current.contains(target));
      const inDesktop =
        !!(expirationDesktopRef.current &&
          target &&
          expirationDesktopRef.current.contains(target));
      if (!inMobile && !inDesktop) {
        setIsExpirationModalOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [isExpirationModalOpen]);

  const frameScale = Math.min(
    1,
    Math.max(0, (viewportWidth - 24) / FRAME_WIDTH),
    Math.max(0, (viewportHeight - SEND_PAGE_DESKTOP_CHROME_PX) / FRAME_HEIGHT),
  );
  const scaledFrameWidth = FRAME_WIDTH * frameScale;
  const scaledFrameHeight = FRAME_HEIGHT * frameScale;
  const expirationDisplay = expirationUnit && expirationCount ? `${expirationCount} ${expirationUnit}` : 'Never';
  const expirationCountOptions = useMemo(() => {
    if (!expirationUnit) return [] as number[];
    return Array.from({length: EXPIRATION_MAX_COUNTS[expirationUnit]}, (_, i) => i + 1);
  }, [expirationUnit]);

  const handleSelectExpirationTime = (unit: ExpirationUnit) => {
    setExpirationUnit(unit);
    setIsTimeBoxSelected(true);
    setIsTimeDropdownOpen(false);
    if (!expirationCount || expirationCount > EXPIRATION_MAX_COUNTS[unit]) {
      setExpirationCount(null);
      setIsCountBoxSelected(false);
    }
  };

  const handleSelectExpirationCount = (value: number) => {
    setExpirationCount(value);
    setIsCountBoxSelected(true);
    setIsCountDropdownOpen(false);
  };

  const resetExpirationSelection = () => {
    setExpirationUnit(null);
    setExpirationCount(null);
    setIsTimeBoxSelected(false);
    setIsCountBoxSelected(false);
    setIsTimeDropdownOpen(false);
    setIsCountDropdownOpen(false);
  };

  const openExpirationPicker = () => {
    if (!isLoggedIn) {
      showGuestNotice();
      return;
    }
    setIsExpirationModalOpen(prev => {
      const next = !prev;
      if (!next) {
        setIsTimeDropdownOpen(false);
        setIsCountDropdownOpen(false);
      }
      return next;
    });
  };

  const handleNeverExpirationClick = () => {
    resetExpirationSelection();
    setIsExpirationModalOpen(false);
  };

  const renderTabButtons = (variant: 'mobile' | 'desktop') => {
    const tabHeight = variant === 'mobile' ? 'h-[52px] w-full text-[16px]' : 'h-[65px] w-full text-[17px]';
    const qrHeight = variant === 'mobile' ? tabHeight : 'h-[77px] w-full text-[17px]';
    const linkHeight = variant === 'mobile' ? tabHeight : 'h-[76px] w-full text-[17px]';
    const activeClass =
      variant === 'mobile'
        ? 'rounded-[20px] border-2 border-primary font-bold text-primary'
        : 'rounded-[24px] border-2 border-primary bg-paper font-bold text-primary';

    return (
      <div className={clsx(variant === 'mobile' ? 'rounded-[24px] border-2 border-divider bg-paper p-[10px]' : 'h-[220px] rounded-[24px] border-2 border-divider bg-paper p-0')}>
        <button
          type="button"
          onClick={() => setActiveMode('share')}
          className={clsx(tabHeight, activeMode === 'share' ? activeClass : 'text-main')}
        >
          Share Files
        </button>
        <button
          type="button"
          onClick={() => {
            if (!isLoggedIn) {
              showGuestNotice();
              return;
            }
            setActiveMode('qr');
            setSendError('');
          }}
          data-guest-lock-trigger
          onMouseDown={e => {
            if (!isLoggedIn) {
              e.preventDefault();
              showGuestNotice();
            }
          }}
          className={clsx(qrHeight, !isLoggedIn && 'cursor-not-allowed text-[#999]', activeMode === 'qr' ? activeClass : 'text-main')}
        >
          Create QR Code
        </button>
        <button
          type="button"
          onClick={() => {
            if (!isLoggedIn) {
              showGuestNotice();
              return;
            }
            setActiveMode('link');
            setSendError('');
          }}
          data-guest-lock-trigger
          onMouseDown={e => {
            if (!isLoggedIn) {
              e.preventDefault();
              showGuestNotice();
            }
          }}
          className={clsx(linkHeight, !isLoggedIn && 'cursor-not-allowed text-[#999]', activeMode === 'link' ? activeClass : 'text-main')}
        >
          Create Link
        </button>
      </div>
    );
  };

  const renderTransferLimitControl = (variant: 'mobile' | 'desktop') => {
    const containerRef = variant === 'mobile' ? transferLimitMobileRef : transferLimitDesktopRef;
    const menuClass =
      variant === 'mobile'
        ? 'absolute left-0 top-[72px] z-[141] w-full rounded-[24px] border-2 border-divider bg-[#f8f9ff] p-[12px] shadow-[1px_1px_30px_1px_rgba(55,55,55,0.2)]'
        : 'absolute left-0 top-[80px] z-[145] w-[350px] rounded-[30px] border-2 border-divider bg-[#f8f9ff] p-[12px] shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)]';
    const rowClass =
      variant === 'mobile'
        ? 'grid h-[56px] w-full grid-cols-[1fr_84px] rounded-[20px] border-2 border-divider bg-paper text-left transition-colors hover:border-primary'
        : 'grid h-[60px] w-full grid-cols-[1fr_83px] rounded-[24px] border-2 border-divider bg-paper text-left transition-colors hover:border-primary';
    const outerClass =
      variant === 'mobile'
        ? 'relative mt-[12px]'
        : 'relative mt-[23px]';
    const buttonClass =
      variant === 'mobile'
        ? 'grid h-[65px] w-full grid-cols-[1fr_88px] rounded-[24px] border-2 bg-paper text-left'
        : 'grid h-[65px] w-full grid-cols-[1fr_88px] rounded-[24px] border-2 bg-paper text-left';

    return (
      <div className={outerClass} ref={containerRef}>
        <button
          type="button"
          onClick={() => {
            setIsTransferLimitMenuOpen(v => !v);
          }}
          className={clsx(buttonClass, isTransferLimitMenuOpen ? 'border-primary' : 'border-divider')}
        >
          <div className={clsx('flex items-center ps-[14px] text-[17px] font-normal', isTransferLimitMenuOpen ? 'text-primary' : 'text-main')}>
            Transfer limit
          </div>
          <div className={clsx('flex items-center justify-center rounded-[22px] text-[17px] font-normal', isTransferLimitMenuOpen ? 'bg-primary text-white' : 'bg-paper border-s-2 border-divider text-main')}>
            {transferLimitLabel}
          </div>
        </button>
        {isTransferLimitMenuOpen && (
          <div className={menuClass}>
            <button type="button" onClick={() => handleTransferLimitItemClick('+20GB', '$2.99')} className={rowClass}>
              <span className="flex items-center ps-[16px] text-[17px] text-main">+20 GB Transfer Limit</span>
              <span className="m-[2px] flex items-center justify-center rounded-[20px] bg-primary text-[17px] text-white">$2.99</span>
            </button>
            <button type="button" onClick={() => handleTransferLimitItemClick('+50GB', '$4.99')} className={clsx('mt-[10px]', rowClass)}>
              <span className="flex items-center ps-[16px] text-[17px] text-main">+50 GB Transfer Limit</span>
              <span className="m-[2px] flex items-center justify-center rounded-[20px] bg-primary text-[17px] text-white">$4.99</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderExpirationControl = (variant: 'mobile' | 'desktop') => {
    const containerRef = variant === 'mobile' ? expirationMobileRef : expirationDesktopRef;
    const outerClass =
      variant === 'mobile'
        ? 'relative mt-[10px] flex h-[65px] rounded-[24px] border-2 bg-paper p-[2px]'
        : 'relative mt-[20px] h-[65px] rounded-[24px] border-2 bg-paper p-[2px]';

    return (
      <div className={variant === 'mobile' ? 'relative z-[320]' : ''} ref={variant === 'mobile' ? containerRef : undefined}>
        <div className={outerClass} ref={variant === 'desktop' ? containerRef : undefined}>
          <div className={variant === 'desktop' ? 'grid h-full grid-cols-2 gap-[6px]' : 'flex h-full w-full'}>
            <button
              type="button"
              data-guest-lock-trigger
              onClick={openExpirationPicker}
              onMouseDown={e => handleGuestLockedControl(e)}
              className={clsx(
                variant === 'mobile' ? 'h-full flex-1 rounded-[20px] text-[16px]' : 'rounded-[20px] text-[17px]',
                !isLoggedIn && 'cursor-not-allowed text-[#999]',
                isLoggedIn && (isExpirationModalOpen ? 'text-primary' : 'text-main'),
              )}
            >
              Expiration
            </button>
            <button
              type="button"
              data-guest-lock-trigger
              onClick={openExpirationPicker}
              onMouseDown={e => handleGuestLockedControl(e)}
              className={clsx(
                variant === 'mobile' ? 'h-full w-[45%] rounded-[20px] text-[16px] font-normal' : 'rounded-[20px] text-[17px] font-normal',
                !isLoggedIn && 'cursor-not-allowed text-[#999]',
                isLoggedIn &&
                  (isExpirationModalOpen
                    ? 'bg-primary text-white'
                    : 'border border-divider bg-paper text-main'),
              )}
            >
              Set
            </button>
          </div>
          {variant === 'desktop' && isExpirationModalOpen && (
            <ExpirationPickerModal
              className="absolute left-0 top-0 z-[140] w-[400px]"
              isMobile={false}
              expirationDisplay={expirationDisplay}
              expirationUnit={expirationUnit}
              expirationCount={expirationCount}
              isTimeBoxSelected={isTimeBoxSelected}
              isCountBoxSelected={isCountBoxSelected}
              isTimeDropdownOpen={isTimeDropdownOpen}
              isCountDropdownOpen={isCountDropdownOpen}
              countOptions={expirationCountOptions}
              onToggleTime={() => {
                setIsTimeBoxSelected(true);
                setIsTimeDropdownOpen(prev => !prev);
                setIsCountDropdownOpen(false);
              }}
              onToggleCount={() => {
                if (!expirationUnit) return;
                setIsCountBoxSelected(true);
                setIsCountDropdownOpen(prev => !prev);
                setIsTimeDropdownOpen(false);
              }}
              onSelectTime={handleSelectExpirationTime}
              onSelectCount={handleSelectExpirationCount}
            />
          )}
          {showExpirationRequiredNotice && !hasSelectedExpiration && variant === 'desktop' && (
            <div className="absolute left-[205px] top-[-1px] z-[146] w-[196px] rounded-[20px] border-2 border-[#EB4242] bg-alt px-[14px] py-[8px] text-[14px] leading-[1.2] text-[#EB4242]">
              You&apos;ll have to select an expiration before you can continue
            </div>
          )}
        </div>
        {variant === 'mobile' && isExpirationModalOpen && (
          <ExpirationPickerModal
            className="mt-[10px] w-full"
            isMobile={true}
            expirationDisplay={expirationDisplay}
            expirationUnit={expirationUnit}
            expirationCount={expirationCount}
            isTimeBoxSelected={isTimeBoxSelected}
            isCountBoxSelected={isCountBoxSelected}
            isTimeDropdownOpen={isTimeDropdownOpen}
            isCountDropdownOpen={isCountDropdownOpen}
            countOptions={expirationCountOptions}
            onToggleTime={() => {
              setIsTimeBoxSelected(true);
              setIsTimeDropdownOpen(prev => !prev);
              setIsCountDropdownOpen(false);
            }}
            onToggleCount={() => {
              if (!expirationUnit) return;
              setIsCountBoxSelected(true);
              setIsCountDropdownOpen(prev => !prev);
              setIsTimeDropdownOpen(false);
            }}
            onSelectTime={handleSelectExpirationTime}
            onSelectCount={handleSelectExpirationCount}
          />
        )}
        {showExpirationRequiredNotice && !hasSelectedExpiration && variant === 'mobile' && (
          <div className="absolute right-[8px] top-[-86px] z-[170] w-[196px] rounded-[20px] border-2 border-[#EB4242] bg-alt px-[14px] py-[8px] text-[14px] leading-[1.2] text-[#EB4242]">
            You&apos;ll have to select an expiration before you can continue
          </div>
        )}
      </div>
    );
  };

  const renderPasswordToggleControl = (variant: 'mobile' | 'desktop') => (
    <div ref={variant === 'mobile' ? passwordMobileRef : passwordDesktopRef}>
      <button
        type="button"
        data-guest-lock-trigger
        onClick={() => {
          if (!isLoggedIn) {
            showGuestNotice();
            return;
          }
          if (!passwordLockedForLinkQr) {
            setIsPasswordEnabled(v => !v);
          }
        }}
        onMouseDown={e => handleGuestLockedControl(e)}
        className={clsx(
          variant === 'mobile' ? 'mt-[10px]' : 'mt-[20px]',
          'flex h-[65px] w-full items-center justify-between rounded-[24px] border-2 bg-paper px-[12px]',
          !isLoggedIn && 'cursor-not-allowed',
          isLoggedIn && (isPasswordEnabled ? 'border-primary' : 'border-divider'),
          !isLoggedIn && 'border-divider',
        )}
      >
        <span
          className={clsx(
            'text-[17px]',
            !isLoggedIn && 'text-[#999]',
            isLoggedIn && (isPasswordEnabled ? 'text-primary' : 'text-main'),
          )}
        >
          {passwordLockedForLinkQr ? 'Password (required)' : 'Add password'}
        </span>
        <span
          className={clsx(
            'relative h-[33px] w-[60px] rounded-[100px] border-2 bg-paper',
            !isLoggedIn && 'border-[#999]',
            isLoggedIn && (isPasswordEnabled ? 'border-primary' : 'border-[#535353]'),
          )}
        >
          <span
            className={clsx(
              'absolute top-[-2px] h-[33px] w-[33px] rounded-full',
              !isLoggedIn && 'left-[-2px] bg-[#999]',
              isLoggedIn && (isPasswordEnabled ? 'left-[27px] bg-primary' : 'left-[-2px] bg-[#535353]'),
            )}
          />
        </span>
      </button>
    </div>
  );

  const renderMessageField = (variant: 'mobile' | 'desktop') => (
    <div
      data-guest-lock-trigger
      onMouseDown={e => handleGuestLockedControl(e)}
      onClick={() => {
        if (!isLoggedIn) showGuestNotice();
      }}
      className={clsx(
        'relative rounded-[24px] border-2 border-transparent bg-alt focus-within:border-primary',
        variant === 'mobile'
          ? 'mt-[10px] h-[228px] w-full'
          : clsx(DESKTOP_FORM_FIELD_STACK_GAP, DESKTOP_MESSAGE_FIELD_CLASS),
        !isLoggedIn && 'cursor-not-allowed focus-within:border-transparent',
      )}
    >
      <textarea
        value={message}
        onChange={e => {
          if (isLoggedIn) setMessage(e.target.value);
        }}
        readOnly={!isLoggedIn}
        tabIndex={isLoggedIn ? 0 : -1}
        maxLength={maxMessageLength}
        className={clsx(
          'w-full resize-none rounded-[24px] bg-transparent text-[17px] placeholder:text-muted outline-none',
          variant === 'mobile'
            ? 'h-[228px] p-[20px] pb-[42px]'
            : 'h-[138px] p-[24px] pb-[28px]',
          !isLoggedIn ? 'cursor-not-allowed text-[#999]' : 'text-main',
        )}
        placeholder="Message (Optional)"
      />
      <p
        className={clsx(
          'absolute text-[14px]',
          variant === 'mobile' ? 'bottom-[10px] right-[16px]' : 'bottom-[8px] right-[24px]',
          !isLoggedIn ? 'text-[#999]' : 'text-main',
        )}
      >
        {maxMessageLength - message.length} characters
      </p>
    </div>
  );

  const renderQrPanel = (variant: 'mobile' | 'desktop') => {
    const isMobile = variant === 'mobile';
    const qrSize = isMobile ? 180 : 220;
    const toolbarDisabled = !isQrGenerated || !lastReceiveUrl || isSending;

    return (
      <div className={clsx('flex flex-col items-center', !isMobile && 'h-full w-full')}>
        <div className={clsx(isMobile ? 'mx-auto flex h-[44px] w-[176px] items-center justify-center gap-[18px] rounded-[18px] border-2 border-divider bg-paper' : 'flex h-[60px] w-[200px] items-center justify-center rounded-[24px] border-2 border-divider bg-paper')}>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={() => void handleDownloadQr()}
            className={clsx('group relative text-[#535353] disabled:opacity-40', !isMobile && 'ms-0')}
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Download
            </span>
            <FileDownloadIcon size="md" />
          </button>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={handlePrintLinkOrQr}
            className={clsx('group relative text-[#535353] disabled:opacity-40', !isMobile && 'ms-[34px]')}
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Print
            </span>
            <PrintIcon size="md" />
          </button>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={handleCopyReceiveUrl}
            className={clsx('group relative text-[#535353] disabled:opacity-40', !isMobile && 'ms-[34px]')}
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Copy
            </span>
            <ContentCopyIcon size="md" />
          </button>
        </div>
        <div
          className={clsx(
            isMobile
              ? 'mx-auto mt-[12px] flex h-[180px] w-[180px] items-center justify-center'
              : 'mt-[68px] flex h-[220px] w-[220px] items-center justify-center',
            'border border-transparent bg-[#f4f4f4] text-center text-[16px] font-medium text-main dark:border-[#353535] dark:bg-[#1f1f1f] dark:text-[#e5e5e5]',
          )}
        >
          {isQrGenerated && lastReceiveUrl ? (
            <SendQrDisplay url={lastReceiveUrl} size={qrSize} className="max-h-full max-w-full object-contain" />
          ) : (
            <>
              QR code will
              <br />
              appear here
            </>
          )}
        </div>
        {isQrGenerated && expiryCountdown ? (
          <p className={clsx(isMobile ? 'mt-[8px] text-center text-[15px] text-main' : 'mt-[8px] text-[17px] text-main')}>
            {expiryCountdown}
          </p>
        ) : (
          <p className={clsx(isMobile ? 'mt-[8px] text-center text-[15px] text-main' : 'mt-[8px] text-[17px] text-main')} />
        )}
        {isQrGenerated ? (
          <div className={clsx(isMobile ? 'mx-auto mt-[12px] flex w-fit items-center gap-[10px]' : 'mt-auto flex items-center gap-[10px]')}>
            <button
              type="button"
              onClick={resetLinkQrResult}
              className={clsx(isMobile ? 'h-[54px] w-[110px]' : 'h-[60px] w-[141px]', 'rounded-[24px] border-2 border-[#2b7e20] bg-[#2b7e20] text-[17px] text-white')}
            >
              New
            </button>
            <button
              type="button"
              onClick={resetLinkQrResult}
              aria-label="Clear QR"
              className={clsx(isMobile ? 'h-[54px] w-[54px]' : 'h-[60px] w-[60px]', 'flex items-center justify-center rounded-[24px] border-2 border-[#2b7e20] bg-paper text-[#535353]')}
            >
              <DeleteOutlineIcon size="md" />
            </button>
            <button
              type="button"
              onClick={() => toast('Send history is coming soon.')}
              aria-label="Send history"
              className={clsx(isMobile ? 'h-[54px] w-[54px]' : 'h-[60px] w-[60px]', 'flex items-center justify-center rounded-[24px] border-2 border-[#2b7e20] bg-paper text-[#535353]')}
            >
              <HistoryIcon size="md" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleGenerateQr}
            disabled={isSending}
            className={clsx(isMobile ? 'mx-auto mt-[12px] block h-[54px] w-[141px]' : 'mt-auto h-[60px] w-[141px]', 'rounded-[24px] border-2 border-divider bg-[#2B7E20] text-[17px] text-white disabled:opacity-70')}
          >
            {isSending ? 'Generating…' : 'Generate'}
          </button>
        )}
        {sendError && activeMode === 'qr' && (
          <p className="mt-[8px] max-w-full px-[8px] text-center text-[13px] font-medium text-[#E34444]">{sendError}</p>
        )}
      </div>
    );
  };

  const renderLinkPanel = (variant: 'mobile' | 'desktop') => {
    const isMobile = variant === 'mobile';
    const toolbarDisabled = !isLinkGenerated || !lastReceiveUrl || isSending;

    return (
      <div className={clsx('flex flex-col items-center', !isMobile && 'h-full w-full')}>
        <div className={clsx(isMobile ? 'mx-auto flex h-[44px] w-[176px] items-center justify-center gap-[18px] rounded-[18px] border-2 border-divider bg-paper' : 'flex h-[60px] w-[200px] items-center justify-center rounded-[24px] border-2 border-divider bg-paper')}>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={handleDownloadLink}
            className="group relative text-[#535353] disabled:opacity-40"
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Download
            </span>
            <FileDownloadIcon size="md" />
          </button>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={handlePrintLinkOrQr}
            className={clsx('group relative text-[#535353] disabled:opacity-40', !isMobile && 'ms-[34px]')}
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Print
            </span>
            <PrintIcon size="md" />
          </button>
          <button
            type="button"
            disabled={toolbarDisabled}
            onClick={handleCopyReceiveUrl}
            className={clsx('group relative text-[#535353] disabled:opacity-40', !isMobile && 'ms-[34px]')}
          >
            <span className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-[16px] border border-divider bg-alt px-[12px] py-[6px] text-[12px] text-main shadow-[0_8px_20px_rgba(15,23,42,0.12)] group-hover:block">
              Copy
            </span>
            <ContentCopyIcon size="md" />
          </button>
        </div>
        {isLinkGenerated && lastReceiveUrl ? (
          <>
            <div
              className={clsx(
                isMobile
                  ? 'mt-[12px] flex min-h-[56px] max-w-full items-center overflow-hidden rounded-[20px] bg-[#f4f4f4] px-[14px] py-[8px] text-[14px] text-[#535353]'
                  : 'mt-[68px] flex h-[65px] w-[411px] items-center overflow-hidden rounded-[24px] bg-[#f4f4f4] px-[20px] text-[16px] text-[#535353]',
              )}
            >
              <a
                href={lastReceiveUrl}
                target="_blank"
                rel="noreferrer"
                className="truncate text-primary underline"
              >
                {lastReceiveUrl}
              </a>
            </div>
            {expiryCountdown && (
              <p className={clsx(isMobile ? 'mt-[10px] text-center text-[15px] text-main' : 'mt-[12px] text-[17px] text-main')}>
                {expiryCountdown}
              </p>
            )}
            <div className={clsx(isMobile ? 'mt-[10px] flex items-center justify-center gap-[10px]' : 'mt-auto flex items-center gap-[10px]')}>
              <button
                type="button"
                onClick={resetLinkQrResult}
                className={clsx(isMobile ? 'h-[54px] w-[110px]' : 'h-[60px] w-[141px]', 'rounded-[24px] border-2 border-[#2b7e20] bg-[#2b7e20] text-[17px] text-white')}
              >
                New
              </button>
              <button
                type="button"
                onClick={resetLinkQrResult}
                aria-label="Clear link"
                className={clsx(isMobile ? 'h-[54px] w-[54px]' : 'h-[60px] w-[60px]', 'flex items-center justify-center rounded-[24px] border-2 border-[#2b7e20] bg-paper text-[#535353]')}
              >
                <DeleteOutlineIcon size="md" />
              </button>
              <button
                type="button"
                onClick={() => toast('Send history is coming soon.')}
                aria-label="Send history"
                className={clsx(isMobile ? 'h-[54px] w-[54px]' : 'h-[60px] w-[60px]', 'flex items-center justify-center rounded-[24px] border-2 border-[#2b7e20] bg-paper text-[#535353]')}
              >
                <HistoryIcon size="md" />
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={handleGenerateLink}
            disabled={isSending}
            className={clsx(isMobile ? 'mx-auto mt-[16px] h-[54px] w-[141px]' : 'mt-auto h-[60px] w-[141px]', 'rounded-[24px] border-2 border-divider bg-[#2B7E20] text-[17px] text-white disabled:opacity-70')}
          >
            {isSending ? 'Generating…' : 'Generate'}
          </button>
        )}
        {sendError && activeMode === 'link' && (
          <p className="mt-[8px] max-w-full px-[8px] text-center text-[13px] font-medium text-[#E34444]">{sendError}</p>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <DefaultMetaTags />
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#14161a]">
        <SiteHeader active="send" />
        <main className="relative z-20 w-full max-w-full overflow-x-hidden px-[6px] pb-[24px] lg:hidden">
          <div className="w-full max-w-full overflow-x-hidden rounded-[24px] border-2 border-divider bg-paper p-[14px]">
            {renderTabButtons('mobile')}
            {showGuestLockedFieldsNotice && !isLoggedIn && (
              <div
                data-guest-lock-popup
                className="mt-[10px] rounded-[24px] border-2 border-[#d93f3f] bg-paper px-[16px] py-[12px] text-[18px] leading-[1.2] text-[#d93f3f]"
              >
                You’ll have to sign in to use these other options
              </div>
            )}

            {activeMode === 'share' && (
              <div ref={shareBoxMobileRef} className="relative mt-[10px]">
                <div className="relative h-[65px] rounded-[24px] border-2 border-transparent bg-alt focus-within:border-primary">
                  <div className="absolute left-0 top-0 h-[65px] w-[calc(100%-65px)] overflow-hidden">
                    <div className="flex h-[65px] items-center px-[12px]">
                      <input
                        type="email"
                        value={emailInput}
                        onChange={e => {
                          setEmailInput(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        onKeyDown={handleEmailKeyDown}
                        autoComplete="off"
                        className="h-[49px] min-w-[120px] w-full bg-transparent text-[17px] text-main placeholder:text-muted outline-none"
                        placeholder="Email"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddRecipientClick}
                    className="absolute right-0 top-[-2px] z-[20] h-[65px] w-[65px] rounded-[24px] border-[3px] border-primary bg-primary"
                    aria-label="Add recipient"
                  >
                    <img src={FIGMA_PLUS_ICON} alt="" className="absolute left-[13px] top-[13px] h-[34px] w-[34px]" />
                  </button>
                </div>
                {isShareBoxVisible && (
                  <div className="absolute right-0 top-[78px] z-[160] h-[240px] w-[calc(100vw-48px)] max-w-[415px] rounded-[24px] border border-divider bg-alt px-[14px] py-[14px] shadow-[0_10px_30px_rgba(15,23,42,0.12)] sm:left-[calc(100%-32px)] sm:right-auto sm:-translate-x-1/2">
                <div className="relative flex min-h-[30px] items-center justify-center">
                  <p className="text-center text-[17px] font-semibold text-main">Share with others</p>
                  <button
                    type="button"
                    onClick={() => setIsShareBoxVisible(false)}
                    className="absolute right-[-26px] top-[-26px] inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[#D8562A] text-white shadow-[0_8px_18px_rgba(216,86,42,0.28)]"
                    aria-label="Close share popup"
                  >
                    <CloseIcon />
                  </button>
                </div>
                <div className="mt-[12px] hidden-scrollbar h-[168px] overflow-y-auto">
                  {sharedRecipients.length ? (
                    <div className="flex flex-wrap gap-[8px]">
                      {sharedRecipients.map(pill => (
                        <span key={pill.id} className="inline-flex h-[36px] max-w-full items-center gap-[6px]">
                          <span className="inline-flex h-[36px] max-w-[260px] items-center gap-[6px] overflow-hidden rounded-[999px] bg-[#FFB7B7] px-[10px]">
                            {pill.hasCloudTransferAccount && (
                              <span className="inline-flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border border-[#d7d7d7] bg-paper p-[1px]">
                                <img src={CT_FAVICON} alt="CT" className="h-full w-full rounded-full object-cover" />
                              </span>
                            )}
                            <span className="min-w-0 truncate text-[14px] text-main">{pill.email}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => setSharedRecipients(prev => prev.filter(item => item.id !== pill.id))}
                            className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#d8562a] text-[14px] text-white"
                            aria-label={`Remove ${pill.email}`}
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="py-[24px] text-center text-[15px] font-semibold text-main">Press (enter) to add more</p>
                  )}
                </div>
                  </div>
                )}
              </div>
            )}

            {renderSenderEmailField()}

            {activeMode === 'share' && (
              <>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="mt-[10px] h-[65px] w-full rounded-[24px] border-2 border-transparent bg-alt px-[20px] text-[17px] text-main placeholder:text-muted outline-none focus:border-primary"
                  placeholder="Label (Optional)"
                />
                {renderMessageField('mobile')}
                <button
                  type="button"
                  onClick={handleSendClick}
                  disabled={isSending}
                  className="mt-[12px] h-[60px] w-full rounded-[24px] bg-primary text-[17px] font-medium text-white disabled:opacity-70"
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
                {sendError && <p className="mt-[8px] text-[13px] font-medium text-[#E34444]">{sendError}</p>}
                {lastReceiveUrl && activeMode !== 'share' && (
                  <a className="mt-[6px] block break-all text-[12px] text-primary underline" href={lastReceiveUrl} target="_blank" rel="noreferrer">{lastReceiveUrl}</a>
                )}
              </>
            )}

            {(activeMode === 'share' || activeMode === 'qr' || activeMode === 'link') && (
              renderTransferLimitControl('mobile')
            )}

            {(activeMode === 'share' || activeMode === 'qr' || activeMode === 'link') && (
              renderExpirationControl('mobile')
            )}

            {(activeMode === 'share' || activeMode === 'qr' || activeMode === 'link') && (
              renderPasswordToggleControl('mobile')
            )}
            {activeMode === 'qr' && (
              <div className="mt-[12px] rounded-[24px] border-2 border-divider bg-paper p-[10px]">
                {renderQrPanel('mobile')}
              </div>
            )}
            {(activeMode === 'share' || activeMode === 'qr' || activeMode === 'link') && isPasswordEnabled && (
              <input
                type="password"
                value={passwordPhrase}
                onChange={e => setPasswordPhrase(e.target.value)}
                autoComplete="off"
                name="transfer-password-mobile"
                className="mt-[10px] h-[65px] w-full rounded-[24px] border-2 border-primary bg-alt px-[20px] text-[17px] text-main placeholder:text-muted outline-none"
                placeholder="Password"
              />
            )}
            {activeMode === 'link' && (
              <div className="mt-[12px] rounded-[24px] border-2 border-divider bg-paper p-[12px]">
                {renderLinkPanel('mobile')}
              </div>
            )}
          </div>

          <section
            {...droppableProps}
            ref={dropRef}
            onClick={() => {
              if (isLoggedIn) {
                setIsAddFilesMenuOpen(v => !v);
              }
            }}
            className={clsx(
              'relative mt-[14px] h-[193px] w-full cursor-pointer rounded-[32px] border-[3px] bg-paper shadow-[1px_1px_30px_2px_rgba(144,144,144,0.22)]',
              hasUploadError
                ? 'border-dashed border-[#E34444]'
                : isDropzoneDragOver || isPageFileDragActive
                  ? 'border-dashed border-primary'
                  : 'border-primary',
            )}
          >
            {!isLoggedIn && (
              <input
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="absolute inset-0 z-[30] h-full w-full cursor-pointer opacity-0"
                aria-label="Upload files"
              />
            )}
            <input type="file" multiple ref={fileInputRef} onChange={handleFileInputChange} className="hidden" />
            {isLoggedIn && isAddFilesMenuOpen && (
              <div ref={uploadBoxMobileRef} className="absolute left-1/2 top-[136px] z-[150] h-[120px] w-[179px] -translate-x-1/2 rounded-[28px] border-2 border-divider bg-[#f8f9ff] shadow-[1px_1px_30px_1px_rgba(55,55,55,0.2)] dark:bg-[#1a1a1a]">
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    openDrivePicker();
                  }}
                  className="block h-[60px] w-full rounded-[24px] border border-transparent text-[17px] leading-[60px] text-main transition-colors hover:border-primary hover:bg-white dark:hover:bg-[#222]"
                >
                  Drive
                </button>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setIsAddFilesMenuOpen(false);
                    fileInputRef.current?.click();
                  }}
                  className="block h-[60px] w-full rounded-[24px] border border-transparent text-[17px] leading-[60px] text-main transition-colors hover:border-primary hover:bg-white dark:hover:bg-[#222]"
                >
                  Computer
                </button>
              </div>
            )}
            {hasUploadError ? (
              <div className="flex h-full flex-col px-[20px] pb-[12px] pt-[12px]">
                <p className="text-right text-[20px] font-semibold leading-none tracking-[0.02em] text-[#E34444]">ERR</p>
                <div className="mx-auto mt-[2px] flex h-[62px] w-[62px] items-center justify-center rounded-full bg-[#535353] shadow-[0_6px_14px_rgba(0,0,0,0.18)]">
                  <RefreshIcon className="text-white" />
                </div>
                <p className="mt-[8px] text-center text-[15px] font-semibold leading-[18px] text-[#E34444]">
                  Problem uploading {failedUploads.length > 1 ? 'files' : 'file'}:
                </p>
                <p
                  className="mx-auto mt-[2px] max-w-[290px] overflow-hidden text-center text-[14px] font-medium leading-[18px] text-[#E34444] break-all"
                  style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}
                >
                  {failedUploadPreview}
                </p>
              </div>
            ) : (
              <>
                <p className="pt-[24px] pe-[20px] text-right text-[20px] font-medium text-main">{Math.round(totalPercentage)}%</p>
                <div className="mx-auto mt-[8px] flex h-[60px] w-[60px] items-center justify-center rounded-[30px] border-[3px] border-dashed border-primary bg-primary">
                  <img src={FIGMA_UPLOAD_ICON} alt="" className="h-[35px] w-[35px]" />
                </div>
                <p className="mt-[12px] text-center text-[17px] font-bold text-main">Click or drop your files here</p>
              </>
            )}
          </section>

          <section className="mt-[14px] min-h-[220px] w-full rounded-[32px] border-2 border-divider bg-paper px-[16px] py-[14px]">
            {combinedSendEntries.length === 0 ? (
              <div className="flex h-[180px] items-center justify-center text-[16px] text-main">Your files will appear here</div>
            ) : (
              <div className="flex flex-col gap-[10px]">
                {selectedDriveEntries.map(entry => (
                  <DriveSelectedFileListItem
                    key={`drive-mobile-${entry.id}`}
                    entry={entry}
                    onRemove={() => removeDriveEntryFromSendList(entry.id)}
                  />
                ))}
                {uploadsArray.map(upload => (
                  <FileListItem key={upload.file.id} upload={upload} />
                ))}
              </div>
            )}
          </section>
        </main>

        <main className="relative z-20 hidden px-[6px] pb-[24px] lg:block lg:px-0">
          {/* Figma frame 286:96 exact desktop canvas size: 1897x876 */}
          <div
            className="mx-auto overflow-hidden"
            style={{width: `${scaledFrameWidth}px`, height: `${scaledFrameHeight}px`}}
          >
          <div
            className="relative origin-top-left bg-[#F5F5F7] dark:bg-[#14161a]"
            style={{
              width: `${FRAME_WIDTH}px`,
              height: `${FRAME_HEIGHT}px`,
              transform: `scale(${frameScale})`,
            }}
          >
            {/* Main left panel - structured layout, no absolute inner positioning */}
            <section className="absolute left-[289px] top-[205px] h-[541px] w-[812px] rounded-[60px] border-2 border-divider bg-paper p-[24px]">
              <div className="grid h-full grid-cols-[210px_1fr]">
                <div className="flex h-full flex-col">
                    {renderTabButtons('desktop')}
                  {showGuestLockedFieldsNotice && !isLoggedIn && (
                    <div
                      data-guest-lock-popup
                      className="absolute left-[-285px] top-[208px] z-[145] w-[330px] rounded-[36px] border-[4px] border-[#d93f3f] bg-paper px-[24px] py-[20px] text-[17px] leading-[1.2] text-[#d93f3f]"
                    >
                      You’ll have to sign in to use these other options
                    </div>
                  )}

                  <>
                      {renderTransferLimitControl('desktop')}
                      {renderExpirationControl('desktop')}
                      {renderPasswordToggleControl('desktop')}
                    </>
                </div>

                <div className="relative flex h-full flex-col items-center">
                  {activeMode === 'share' && (
                    <div className={clsx('flex flex-col', DESKTOP_FORM_FIELD_CLASS)}>
                      <div ref={shareBoxDesktopRef} className="relative w-full">
                        <div className="relative h-[65px] w-full rounded-[24px] border-2 border-transparent bg-alt focus-within:border-primary">
                          <div className="absolute left-0 top-0 h-[65px] w-[calc(100%-65px)] overflow-hidden">
                            <div className="flex h-[65px] items-center px-[12px]">
                              <input
                                type="email"
                                value={emailInput}
                                onChange={e => {
                                  setEmailInput(e.target.value);
                                  if (emailError) setEmailError('');
                                }}
                                onKeyDown={handleEmailKeyDown}
                                autoComplete="off"
                                className="h-[49px] min-w-[120px] w-full bg-transparent text-[17px] text-main placeholder:text-muted outline-none"
                                placeholder="Email"
                              />
                            </div>
                          </div>
                          <button type="button" onClick={handleAddRecipientClick} className="absolute right-0 top-[-2px] z-[20] h-[65px] w-[65px] rounded-[24px] border-[3px] border-primary bg-primary" aria-label="Add recipient">
                            <img src={FIGMA_PLUS_ICON} alt="" className="absolute left-[13px] top-[13px] h-[34px] w-[34px]" />
                          </button>
                        </div>
                        {isShareBoxVisible && (
                          <div className="absolute left-[calc(100%-32px)] top-[76px] z-[160] h-[240px] w-[415px] -translate-x-1/2 rounded-[24px] border border-divider bg-alt px-[14px] py-[14px] shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
                          <div className="relative flex min-h-[30px] items-center justify-center">
                            <p className="text-center text-[17px] font-semibold text-main">Share with others</p>
                            <button
                              type="button"
                              onClick={() => setIsShareBoxVisible(false)}
                              className="absolute right-[-26px] top-[-26px] inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[#D8562A] text-white shadow-[0_8px_18px_rgba(216,86,42,0.28)]"
                              aria-label="Close share popup"
                            >
                              <CloseIcon />
                            </button>
                          </div>
                          <div className="mt-[12px] hidden-scrollbar h-[168px] overflow-y-auto">
                            {sharedRecipients.length ? (
                              <div className="flex flex-wrap gap-[8px]">
                                {sharedRecipients.map(pill => (
                                  <span key={pill.id} className="inline-flex h-[36px] max-w-full items-center gap-[6px]">
                                    <span className="inline-flex h-[36px] max-w-[260px] items-center gap-[6px] overflow-hidden rounded-[999px] bg-[#FFB7B7] px-[10px]">
                                      {pill.hasCloudTransferAccount && (
                                        <span className="inline-flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border border-[#d7d7d7] bg-paper p-[1px]">
                                          <img src={CT_FAVICON} alt="CT" className="h-full w-full rounded-full object-cover" />
                                        </span>
                                      )}
                                      <span className="min-w-0 truncate text-[14px] text-main">{pill.email}</span>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setSharedRecipients(prev => prev.filter(item => item.id !== pill.id))}
                                      className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#d8562a] text-[14px] text-white"
                                      aria-label={`Remove ${pill.email}`}
                                    >
                                      x
                                    </button>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="py-[24px] text-center text-[15px] font-semibold text-main">Press (enter) to add more</p>
                            )}
                          </div>
                          </div>
                        )}
                      </div>
                      {renderSenderEmailField('desktop')}
                      <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className={clsx(DESKTOP_FORM_FIELD_STACK_GAP, 'h-[65px] rounded-[24px] border-2 border-transparent bg-alt px-[20px] text-[17px] text-main placeholder:text-muted outline-none focus:border-primary')} placeholder="Label (Optional)" />
                      {renderMessageField('desktop')}
                      <button type="button" onClick={handleSendClick} disabled={isSending} className={clsx(DESKTOP_FORM_FIELD_STACK_GAP, 'h-[60px] w-[156px] self-center rounded-[24px] bg-primary text-[17px] font-medium text-white disabled:opacity-70')}>
                        {isSending ? 'Sending...' : 'Send'}
                      </button>
                      {(sendError || (lastReceiveUrl && activeMode !== 'share')) && (
                        <div className="mt-[6px] self-center text-center">
                          {sendError && <p className="text-[13px] font-medium text-[#E34444]">{sendError}</p>}
                          {lastReceiveUrl && activeMode !== 'share' && (
                            <a className="mt-[4px] block break-all text-[12px] text-primary underline" href={lastReceiveUrl} target="_blank" rel="noreferrer">{lastReceiveUrl}</a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeMode === 'qr' && (
                    renderQrPanel('desktop')
                  )}

                  {activeMode === 'link' && (
                    renderLinkPanel('desktop')
                  )}

                </div>
              </div>
            </section>

            {/* Upload drop zone - node 286:119 */}
            <section
              {...droppableProps}
              ref={dropRef}
              onClick={() => {
                if (isLoggedIn) {
                  setIsAddFilesMenuOpen(v => !v);
                }
              }}
              className={clsx(
                'absolute left-[1191px] top-[205px] h-[193px] w-[400px] cursor-pointer rounded-[60px] border-[3px] bg-paper shadow-[1px_1px_50px_5px_rgba(144,144,144,0.25)]',
                hasUploadError
                  ? 'border-dashed border-[#E34444]'
                  : isDropzoneDragOver || isPageFileDragActive
                    ? 'border-dashed border-primary'
                    : 'border-primary',
              )}
            >
              {!isLoggedIn && (
                <input
                  type="file"
                  multiple
                  onChange={handleFileInputChange}
                  className="absolute inset-0 z-[30] h-full w-full cursor-pointer opacity-0"
                  aria-label="Upload files"
                />
              )}
              <input type="file" multiple ref={fileInputRef} onChange={handleFileInputChange} className="hidden" />
              {isLoggedIn && isAddFilesMenuOpen && (
                <div
                  ref={uploadBoxDesktopRef}
                  className="absolute left-1/2 top-[120px] z-[150] h-[120px] w-[179px] -translate-x-1/2 rounded-[28px] border-2 border-divider bg-[#f8f9ff] shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:bg-[#1a1a1a]"
                >
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      openDrivePicker();
                    }}
                    className="block h-[60px] w-full rounded-[24px] border border-transparent text-[17px] leading-[60px] text-main transition-colors hover:border-primary hover:bg-white dark:hover:bg-[#222]"
                  >
                    Drive
                  </button>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setIsAddFilesMenuOpen(false);
                      fileInputRef.current?.click();
                    }}
                    className="block h-[60px] w-full rounded-[24px] border border-transparent text-[17px] leading-[60px] text-main transition-colors hover:border-primary hover:bg-white dark:hover:bg-[#222]"
                  >
                    Computer
                  </button>
                </div>
              )}
              {hasUploadError ? (
                <div className="absolute inset-0 flex flex-col px-[30px] pb-[14px] pt-[16px]">
                  <p className="text-right text-[20px] font-semibold tracking-[0.02em] leading-none text-[#E34444]">
                    ERR
                  </p>
                  <div className="mx-auto mt-[2px] flex h-[64px] w-[64px] items-center justify-center rounded-full bg-[#535353] shadow-[0_8px_18px_rgba(0,0,0,0.2)]">
                    <RefreshIcon className="text-white" />
                  </div>
                  <p className="mt-[8px] text-[16px] font-semibold leading-[20px] text-[#E34444]">
                    Problem uploading {failedUploads.length > 1 ? 'files' : 'file'}:
                  </p>
                  <p
                    className="mt-[2px] max-w-[328px] overflow-hidden text-[15px] font-medium leading-[19px] text-[#E34444] break-all"
                    style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}
                  >
                    {failedUploadPreview}
                  </p>
                </div>
              ) : (
                <>
                  <p className="absolute left-[329px] top-[46px] text-[20px] font-medium leading-[normal] text-main">
                    {Math.round(totalPercentage)}%
                  </p>
                  {/* Figma node 286:135 + 325:9 */}
                  <div className="absolute left-[170px] top-[67px] h-[60px] w-[60px] rounded-[30px] border-[3px] border-dashed border-primary bg-primary">
                    <img src={FIGMA_UPLOAD_ICON} alt="" className="absolute left-[12px] top-[12px] h-[35px] w-[35px]" />
                  </div>
                  <p className="absolute left-[84px] top-[145px] text-[17px] font-bold leading-[normal] text-main">
                    Click or drop your files here
                  </p>
                </>
              )}
            </section>

            {/* Uploaded files panel - node 286:130 */}
            <section className="absolute left-[1191px] top-[430px] h-[316px] w-[400px] rounded-[60px] border-2 border-divider bg-paper px-[20px] py-[16px]">
              {combinedSendEntries.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <p className="text-[16px] font-normal leading-[normal] text-main">Your files will appear here</p>
                </div>
              ) : (
                <div className="flex h-full flex-col gap-[10px] overflow-y-auto pe-[4px] [scrollbar-color:#E9ECF0_transparent] [&::-webkit-scrollbar]:w-[8px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-alt [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-button]:h-0 [&::-webkit-scrollbar-button]:w-0">
                  {selectedDriveEntries.map(entry => (
                    <DriveSelectedFileListItem
                      key={`drive-desktop-${entry.id}`}
                      entry={entry}
                      onRemove={() => removeDriveEntryFromSendList(entry.id)}
                    />
                  ))}
                  {uploadsArray.map(upload => (
                    <FileListItem key={upload.file.id} upload={upload} />
                  ))}
                </div>
              )}
            </section>

            {/* Password field appears only when toggle is ON - node 286:153 */}
            {isPasswordEnabled && (
              <div className="absolute left-[214px] top-[736px] h-[65px] w-[411px] rounded-[24px] border-2 border-primary bg-alt shadow-[1px_1px_50px_1px_rgba(144,144,144,0.25)]">
                <input
                  type="password"
                  value={passwordPhrase}
                  onChange={e => setPasswordPhrase(e.target.value)}
                  autoComplete="off"
                  name="transfer-password-desktop"
                  className="h-[65px] w-[411px] rounded-[24px] bg-transparent px-[20px] text-[17px] font-normal leading-[normal] text-main placeholder:text-muted outline-none"
                  placeholder="Password"
                />
              </div>
            )}
          </div>
          </div>
        </main>

        <TransferAddonCheckoutDialog
          open={transferCheckoutDialog.open}
          sku={transferCheckoutDialog.sku}
          price={transferCheckoutDialog.price}
          limit={transferCheckoutDialog.limit}
          guestEmail={isLoggedIn ? undefined : senderEmail.trim()}
          onClose={() =>
            setTransferCheckoutDialog(prev => ({...prev, open: false}))
          }
        />

        {isDrivePickerPopupOpen && (
          <div className="fixed inset-0 z-[185] bg-[rgba(83,83,83,0.2)] px-[12px] py-[24px] md:px-[20px]">
            <div className="relative mx-auto mt-[8vh] w-full max-w-[1100px] rounded-[20px] border-2 border-[#e9ecf0] bg-white px-[14px] pb-[14px] pt-[10px] shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:border-[#353535] dark:bg-[#1a1a1a] md:rounded-[24px] md:px-[20px] md:pb-[18px] md:pt-[12px]">
              <button
                type="button"
                onClick={() => setIsDrivePickerPopupOpen(false)}
                className="absolute right-[-10px] top-[-10px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#d8562a] text-[14px] leading-none text-white md:right-[-14px] md:top-[-14px] md:h-[30px] md:w-[30px] md:text-[16px]"
                aria-label="Close drive popup"
              >
                <CloseIcon className="text-white" />
              </button>

              <div className="grid h-[40px] grid-cols-[1fr_auto] items-center gap-[8px] rounded-[18px] border-2 border-[#e9ecf0] bg-white px-[6px] dark:border-[#353535] dark:bg-[#1f1f1f] md:h-[60px] md:grid-cols-[1fr_60px_405px] md:rounded-[24px] md:px-[8px]">
                <input
                  type="text"
                  value={drivePickerSearch}
                  onChange={e => setDrivePickerSearch(e.target.value)}
                  placeholder="Search"
                  className="h-full w-full bg-transparent text-[14px] text-[#535353] placeholder:text-[#989898] outline-none dark:text-[#e5e5e5] dark:placeholder:text-[#a3a3a3] md:text-[16px]"
                />
                <div className="hidden h-[45px] w-[45px] items-center justify-center rounded-[18px] bg-[#e9ecf0] dark:bg-[#2a2a2a] md:flex">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-[#535353] dark:text-[#e5e5e5]"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <div className="hidden h-[60px] grid-cols-4 items-center rounded-[24px] border-2 border-[#e9ecf0] ps-[8px] pe-[2px] text-[16px] font-medium text-[#535353] dark:border-[#353535] dark:text-[#e5e5e5] md:grid">
                  <div
                    className="relative flex h-full items-center justify-center"
                    onMouseEnter={() => setIsLocationMenuOpen(true)}
                    onMouseLeave={() => setIsLocationMenuOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setIsLocationMenuOpen(v => !v)}
                      className={clsx(
                        'inline-flex h-[33px] cursor-pointer items-center justify-center rounded-[14px] border-2 px-[10px] transition-colors',
                        isLocationMenuOpen ? 'border-[#2b7e20]' : 'border-transparent',
                      )}
                    >
                      {drivePickerLocation}
                    </button>
                    {isLocationMenuOpen && (
                      <div className="absolute left-0 top-[41px] z-[210] w-[179px] rounded-[24px] border-2 border-[#e9ecf0] bg-[#f8f9ff] shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:border-[#353535] dark:bg-[#1a1a1a]">
                        {(
                          ['All files', 'Tags', 'Favorites', 'Shared', 'Recent'] as const
                        ).map((item, index, items) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => {
                              setDrivePickerLocation(item);
                              setDrivePickerFolderStack([]);
                              setIsLocationMenuOpen(false);
                            }}
                            className={clsx(
                              'mx-auto block h-[60px] w-[calc(100%-16px)] rounded-[22px] border-2 text-center text-[17px] transition-colors',
                              item === drivePickerLocation
                                ? 'border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]'
                                : 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:bg-white hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]',
                              index === 0 && 'rounded-t-[22px]',
                              index === items.length - 1 && 'rounded-b-[22px]',
                            )}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div
                    className="relative flex h-full items-center justify-center"
                    onMouseEnter={() => {
                      setIsDrivePickerTypeMenuOpen(true);
                      setIsDrivePickerOwnerMenuOpen(false);
                      setIsDrivePickerTimeMenuOpen(false);
                    }}
                    onMouseLeave={() => setIsDrivePickerTypeMenuOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setIsDrivePickerTypeMenuOpen(v => !v);
                        setIsDrivePickerOwnerMenuOpen(false);
                        setIsDrivePickerTimeMenuOpen(false);
                      }}
                      className={clsx(
                        'inline-flex h-[33px] items-center justify-center rounded-[14px] border-2 px-[10px] transition-colors',
                        isDrivePickerTypeMenuOpen || drivePickerTypeFilter
                          ? 'border-[#2b7e20] text-[#2b7e20]'
                          : 'border-transparent',
                      )}
                    >
                      Type
                    </button>
                    {isDrivePickerTypeMenuOpen && (
                      <div className="absolute left-1/2 top-[41px] z-[210] max-h-[260px] w-[179px] -translate-x-1/2 overflow-y-auto rounded-[24px] border-2 border-[#e9ecf0] bg-[#f8f9ff] p-1 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:border-[#353535] dark:bg-[#1a1a1a]">
                        {drivePickerTypeOptions.length ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setDrivePickerTypeFilter(null);
                                setIsDrivePickerTypeMenuOpen(false);
                              }}
                              className={clsx(
                                'block h-[48px] w-full rounded-[20px] border-2 text-center text-[16px] transition-colors',
                                drivePickerTypeFilter
                                  ? 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:bg-white hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]'
                                  : 'border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]',
                              )}
                            >
                              All types
                            </button>
                            {drivePickerTypeOptions.map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => {
                                  setDrivePickerTypeFilter(
                                    drivePickerTypeFilter?.toLowerCase() ===
                                      type.toLowerCase()
                                      ? null
                                      : type,
                                  );
                                  setIsDrivePickerTypeMenuOpen(false);
                                }}
                                className={clsx(
                                  'block h-[48px] w-full rounded-[20px] border-2 text-center text-[16px] transition-colors',
                                  drivePickerTypeFilter?.toLowerCase() ===
                                    type.toLowerCase()
                                    ? 'border-2 border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]'
                                    : 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:bg-white hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]',
                                )}
                              >
                                {type}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="flex min-h-[72px] items-center justify-center rounded-[20px] px-4 text-center text-[15px] text-[#989898] dark:text-[#a3a3a3]">
                            No types found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="relative flex h-full items-center justify-center"
                    onMouseEnter={() => {
                      setIsDrivePickerOwnerMenuOpen(true);
                      setIsDrivePickerTypeMenuOpen(false);
                      setIsDrivePickerTimeMenuOpen(false);
                    }}
                    onMouseLeave={() => setIsDrivePickerOwnerMenuOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setIsDrivePickerOwnerMenuOpen(v => !v);
                        setIsDrivePickerTypeMenuOpen(false);
                        setIsDrivePickerTimeMenuOpen(false);
                      }}
                      className={clsx(
                        'inline-flex h-[33px] items-center justify-center rounded-[14px] border-2 px-[10px] transition-colors',
                        isDrivePickerOwnerMenuOpen || drivePickerOwnerFilter
                          ? 'border-[#2b7e20] text-[#2b7e20]'
                          : 'border-transparent',
                      )}
                    >
                      Owner
                    </button>
                    {isDrivePickerOwnerMenuOpen && (
                      <div className="absolute left-1/2 top-[41px] z-[210] max-h-[260px] w-[265px] -translate-x-1/2 overflow-y-auto rounded-[24px] border-2 border-[#e9ecf0] bg-[#f8f9ff] p-2 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:border-[#353535] dark:bg-[#1a1a1a]">
                        {drivePickerOwnerOptions.length ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setDrivePickerOwnerFilter(null);
                                setIsDrivePickerOwnerMenuOpen(false);
                              }}
                              className="block w-full rounded-[24px] border-2 border-transparent px-4 py-[10px] text-center text-[16px] text-[#535353] hover:border-[#2b7e20] hover:bg-white hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]"
                            >
                              All owners
                            </button>
                            {drivePickerOwnerOptions.map(option => (
                              <button
                                key={option.email}
                                type="button"
                                onClick={() => {
                                  setDrivePickerOwnerFilter(
                                    drivePickerOwnerFilter?.toLowerCase() ===
                                      option.email.toLowerCase()
                                      ? null
                                      : option.email,
                                  );
                                  setIsDrivePickerOwnerMenuOpen(false);
                                }}
                                className={clsx(
                                  'w-full rounded-[24px] border-2 px-4 py-[10px] text-center text-[16px] transition-colors',
                                  drivePickerOwnerFilter?.toLowerCase() ===
                                    option.email.toLowerCase()
                                    ? 'border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]'
                                    : 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:bg-white hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]',
                                )}
                              >
                                {option.label}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="flex min-h-[72px] items-center justify-center rounded-[20px] px-4 text-center text-[15px] text-[#989898] dark:text-[#a3a3a3]">
                            No shared users found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="relative flex h-full items-center justify-center"
                    onMouseEnter={() => {
                      setIsDrivePickerTimeMenuOpen(true);
                      setIsDrivePickerTypeMenuOpen(false);
                      setIsDrivePickerOwnerMenuOpen(false);
                    }}
                    onMouseLeave={() => setIsDrivePickerTimeMenuOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setIsDrivePickerTimeMenuOpen(v => !v);
                        setIsDrivePickerTypeMenuOpen(false);
                        setIsDrivePickerOwnerMenuOpen(false);
                      }}
                      className={clsx(
                        'inline-flex h-[33px] items-center justify-center rounded-[14px] border-2 px-[10px] transition-colors',
                        isDrivePickerTimeMenuOpen ||
                          drivePickerTimeFilter.kind !== 'all'
                          ? 'border-[#2b7e20] text-[#2b7e20]'
                          : 'border-transparent',
                      )}
                    >
                      Time
                    </button>
                    {isDrivePickerTimeMenuOpen && (
                      <div className="absolute left-1/2 top-[41px] z-[210] flex max-h-[260px] w-[179px] -translate-x-1/2 flex-col gap-1 overflow-y-auto rounded-[24px] border-2 border-[#e9ecf0] bg-[#f8f9ff] py-1 shadow-[1px_1px_50px_1px_rgba(55,55,55,0.25)] dark:border-[#353535] dark:bg-[#1a1a1a]">
                        {[
                          {kind: 'today', label: 'Today'},
                          {kind: 'pastWeek', label: 'Past week'},
                          {kind: 'pastMonth', label: 'Past month'},
                        ].map(option => (
                          <button
                            key={option.kind}
                            type="button"
                            onClick={() => {
                              setDrivePickerTimeFilter(
                                drivePickerTimeFilter.kind === option.kind
                                  ? {kind: 'all', label: 'Time'}
                                  : (option as HeaderTimeFilter),
                              );
                              setIsDrivePickerTimeMenuOpen(false);
                            }}
                            className={clsx(
                              'mx-auto flex w-[calc(100%-24px)] items-center justify-center rounded-[24px] border-2 py-[10px] text-[16px] transition-colors',
                              drivePickerTimeFilter.kind === option.kind
                                ? 'border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]'
                                : 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]',
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                        {drivePickerYearOptions.map(year => (
                          <button
                            key={year}
                            type="button"
                            onClick={() => {
                              setDrivePickerTimeFilter(
                                drivePickerTimeFilter.kind === 'year' &&
                                  drivePickerTimeFilter.year === year
                                  ? {kind: 'all', label: 'Time'}
                                  : {kind: 'year', year, label: String(year)},
                              );
                              setIsDrivePickerTimeMenuOpen(false);
                            }}
                            className={clsx(
                              'mx-auto flex w-[calc(100%-24px)] items-center justify-center rounded-[24px] border-2 py-[10px] text-[16px] transition-colors',
                              drivePickerTimeFilter.kind === 'year' &&
                                drivePickerTimeFilter.year === year
                                ? 'border-[#2b7e20] bg-white text-[#2b7e20] dark:bg-[#222]'
                                : 'border-transparent text-[#535353] hover:border-[#2b7e20] hover:text-[#2b7e20] dark:text-[#e5e5e5] dark:hover:bg-[#222]',
                            )}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button type="button" className="md:hidden text-[14px] text-[#535353] dark:text-[#e5e5e5]">
                  Filters
                </button>
              </div>

              <div className="mt-[8px] rounded-[16px] bg-[#e9ecf0] px-[10px] py-[8px] text-[13px] text-[#535353] dark:bg-[#222] dark:text-[#e5e5e5] md:mt-[10px] md:rounded-[22px] md:px-[14px] md:py-[10px] md:text-[16px] md:font-medium">
                <div className="flex items-center justify-between gap-[10px] border-b border-[#d6dce3] pb-[8px] text-[12px] dark:border-[#353535] md:text-[14px]">
                  <div className="min-w-0">
                    <span className="font-semibold text-[#2f3a45] dark:text-[#f0f0f0]">Location:</span>{' '}
                    <span className="truncate text-[#535353] dark:text-[#e5e5e5]">
                      {activeDrivePickerFolderLabel}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={goBackDriveFolder}
                    disabled={
                      drivePickerLocation !== 'All files' ||
                      drivePickerFolderStack.length === 0
                    }
                    className={clsx(
                      'rounded-full border border-[#cdd5df] px-[10px] py-[4px] text-[11px] font-medium transition-colors dark:border-[#444] md:text-[12px]',
                      drivePickerLocation !== 'All files' ||
                        drivePickerFolderStack.length === 0
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:border-[#2b7e20] hover:text-[#2b7e20]',
                    )}
                  >
                    Back
                  </button>
                </div>
              </div>

              <div className="mt-[8px] rounded-[16px] bg-[#e9ecf0] px-[10px] py-[8px] text-[13px] text-[#535353] dark:bg-[#222] dark:text-[#e5e5e5] md:mt-[10px] md:rounded-[22px] md:px-[14px] md:py-[10px] md:text-[16px] md:font-medium">
                <div className="grid grid-cols-[1.5fr_0.95fr_0.75fr_0.9fr_0.7fr_1.2fr] items-center gap-[10px] md:grid-cols-[29px_1.55fr_0.85fr_0.72fr_0.9fr_0.65fr_1.45fr] md:gap-[8px]">
                  <span className="hidden md:block" />
                  <span>Name</span>
                  <span>File Type</span>
                  <span>Size</span>
                  <span>Modified</span>
                  <span>Tags</span>
                  <span>Owner</span>
                </div>
              </div>

              <div className="mt-[8px] max-h-[44vh] space-y-[6px] overflow-y-auto pe-[2px] md:mt-[10px] md:max-h-[52vh] md:space-y-[8px]">
                {isDrivePickerLoading ? (
                  <div className="rounded-[16px] border-2 border-[#e9ecf0] bg-white px-[14px] py-[24px] text-center text-[15px] text-[#535353] dark:border-[#353535] dark:bg-[#1f1f1f] dark:text-[#e5e5e5]">
                    Loading Drive files...
                  </div>
                ) : filteredDrivePickerRows.length ? (
                  filteredDrivePickerRows.map(entry => {
                    const ownerLabel = getDriveEntryOwner(entry, user?.email);
                    const fileType = mapDriveEntryToFileType(entry);
                    const isSelected = selectedDriveEntries.some(
                      item => item.id === entry.id,
                    );

                    return (
                      <button
                        type="button"
                        key={entry.id}
                        onClick={() => {
                          if (entry.type !== 'folder') {
                            addDriveEntryToSendList(entry);
                          }
                        }}
                        onDoubleClick={() => {
                          if (entry.type === 'folder') {
                            openDriveFolder(entry);
                          }
                        }}
                        className={clsx(
                          'grid min-h-[34px] w-full grid-cols-[18px_1.55fr_0.85fr_0.72fr_0.9fr_0.65fr_1.45fr] items-center gap-[8px] rounded-[12px] border-2 px-[8px] py-[6px] text-left text-[11px] text-[#535353] transition-colors dark:text-[#e5e5e5] md:min-h-[45px] md:grid-cols-[29px_1.55fr_0.85fr_0.72fr_0.9fr_0.65fr_1.45fr] md:rounded-[16px] md:px-[14px] md:py-[8px] md:gap-[8px] md:text-[16px]',
                          isSelected
                            ? 'border-[#2b7e20] bg-[#f4fbf2] dark:bg-[#1d2a1b]'
                            : entry.type === 'folder'
                              ? 'border-[#e9ecf0] bg-white hover:border-[#2b7e20] cursor-pointer dark:border-[#353535] dark:bg-[#1f1f1f] dark:hover:border-[#2b7e20]'
                              : 'border-[#e9ecf0] bg-white hover:border-[#2b7e20] dark:border-[#353535] dark:bg-[#1f1f1f] dark:hover:border-[#2b7e20]',
                        )}
                      >
                        <FileTypeIcon
                          type={fileType}
                          className="h-[14px] w-[14px] md:h-[29px] md:w-[29px]"
                        />
                        <span className="truncate">{entry.name}</span>
                        <span>{entry.extension || entry.type || ''}</span>
                        <span>{formatDriveSize(entry.file_size)}</span>
                        <span>{formatDriveDate(entry.updated_at)}</span>
                        <div
                          className="grid w-fit grid-cols-4 content-center gap-[4px]"
                          aria-label="Tags"
                        >
                          {(entry.tags || []).map((tag, index) => (
                            <span
                              key={`${entry.id}-${tag.name || 'tag'}-${index}`}
                              className="h-[10px] w-[10px] rounded-full md:h-[14px] md:w-[14px]"
                              style={{
                                backgroundColor: getDriveTagColor(tag.name),
                              }}
                            />
                          ))}
                        </div>
                        <span className="flex items-center gap-[6px] truncate">
                          {ownerLabel === 'You' && (
                            <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#d7eeff] text-[10px] text-[#3ea4f2] dark:bg-[#203247] dark:text-[#8ecaff]">
                              Y
                            </span>
                          )}
                          <span className="truncate">{ownerLabel}</span>
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <div className="rounded-[16px] border-2 border-[#e9ecf0] bg-white px-[14px] py-[24px] text-center text-[15px] text-[#535353] dark:border-[#353535] dark:bg-[#1f1f1f] dark:text-[#e5e5e5]">
                    No Drive files match this filter yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <GuestSendOtpModal
          open={isOtpModalOpen}
          senderEmail={senderEmail}
          otpCode={otpCode}
          onOtpChange={setOtpCode}
          error={otpError}
          isSubmitting={isOtpSubmitting}
          isResending={isOtpResending}
          onClose={() => {
            setIsOtpModalOpen(false);
            setOtpCode('');
            setOtpError('');
          }}
          onSubmit={() => void handleGuestVerifyAndSend()}
          onResend={() => void handleGuestResendOtp()}
        />
      </div>
    </Fragment>
  );
}

interface ExpirationPickerModalProps {
  className?: string;
  isMobile?: boolean;
  expirationDisplay: string;
  expirationUnit: ExpirationUnit | null;
  expirationCount: number | null;
  isTimeBoxSelected: boolean;
  isCountBoxSelected: boolean;
  isTimeDropdownOpen: boolean;
  isCountDropdownOpen: boolean;
  countOptions: number[];
  onToggleTime: () => void;
  onToggleCount: () => void;
  onSelectTime: (unit: ExpirationUnit) => void;
  onSelectCount: (count: number) => void;
}

function ExpirationPickerModal({
  className,
  isMobile = false,
  expirationDisplay,
  expirationUnit,
  expirationCount,
  isTimeBoxSelected,
  isCountBoxSelected,
  isTimeDropdownOpen,
  isCountDropdownOpen,
  countOptions,
  onToggleTime,
  onToggleCount,
  onSelectTime,
  onSelectCount,
}: ExpirationPickerModalProps) {
  return (
    <div className={clsx('relative z-[260] rounded-[32px] border border-divider bg-alt px-[20px] py-[16px] shadow-[0_10px_30px_rgba(15,23,42,0.12)]', className)}>
      <div className="flex justify-center">
        <div className="h-[65px] min-w-[150px] rounded-[24px] border-2 border-primary bg-paper px-[18px] text-center text-[17px] font-bold leading-[61px] text-main">
          {expirationDisplay}
        </div>
      </div>

      <div className="mt-[18px] flex items-start gap-[14px]">
        <div className="relative flex-1">
          <div className={clsx('flex h-[65px] overflow-hidden rounded-[24px] border-2 bg-paper', isTimeBoxSelected ? 'border-primary' : 'border-divider')}>
            <button type="button" onClick={onToggleTime} className="flex-1 text-left ps-[20px] text-[17px] font-normal text-main">
              {expirationUnit ?? 'Time'}
            </button>
            <button type="button" onClick={onToggleTime} className="flex h-full w-[65px] items-center justify-center border-s-2 border-divider">
              <span className="block h-0 w-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-[#535353]" />
            </button>
          </div>
          {isTimeDropdownOpen && (
            <div
              className={clsx(
                'w-full rounded-[20px] border border-divider bg-paper py-[6px] shadow-[0_10px_24px_rgba(15,23,42,0.12)]',
                isMobile ? 'mt-[6px]' : 'absolute left-0 top-[70px] z-[300]',
              )}
            >
              {EXPIRATION_UNITS.map(unit => (
                <button
                  key={unit}
                  type="button"
                  onClick={() => onSelectTime(unit)}
                  className="block w-full px-[14px] py-[8px] text-left text-[16px] text-main hover:bg-[#F5F5F7]"
                >
                  {unit}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative flex-1">
          <div className={clsx('flex h-[65px] overflow-hidden rounded-[24px] border-2 bg-paper', isCountBoxSelected ? 'border-primary' : 'border-divider')}>
            <button type="button" onClick={onToggleCount} className="flex-1 text-center text-[17px] font-bold text-main">
              {expirationCount ?? 0}
            </button>
            <button
              type="button"
              onClick={onToggleCount}
              disabled={!expirationUnit}
              className={clsx('flex h-full w-[65px] items-center justify-center border-s-2 border-divider', !expirationUnit && 'opacity-50')}
            >
              <span className="block h-0 w-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-[#535353]" />
            </button>
          </div>
          {isCountDropdownOpen && expirationUnit && (
            <div
              className={clsx(
                'hidden-scrollbar max-h-[180px] w-full overflow-y-auto rounded-[20px] border border-divider bg-paper py-[6px] shadow-[0_10px_24px_rgba(15,23,42,0.12)]',
                isMobile ? 'mt-[6px]' : 'absolute left-0 top-[70px] z-[300]',
              )}
            >
              {countOptions.map(count => (
                <button
                  key={count}
                  type="button"
                  onClick={() => onSelectCount(count)}
                  className="block w-full px-[14px] py-[8px] text-center text-[16px] text-main hover:bg-[#F5F5F7]"
                >
                  {count}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileListItem({upload}: {upload: any}) {
  const abortUpload = useFileUploadStore(s => s.abortUpload);
  const removeUpload = useFileUploadStore(s => s.removeUpload);
  const uploadPercentage = Math.max(0, Math.min(100, Math.round(upload.percentage || 0)));
  const fileSizeLabel = prettyBytes(upload.file.size).replace('kB', 'KB');
  const isInProgress = upload.status === 'inProgress';
  const isFailed = upload.status === 'failed';
  const isComplete = !isInProgress && !isFailed;

  return (
    <div
      className={clsx(
        'shrink-0 grid h-[74px] grid-cols-[32px,1fr,32px] items-center gap-[10px] rounded-[18px] border-2 px-[10px] transition-colors',
        isInProgress && 'border-[#CFE7CB] bg-[#FBFFFA]',
        isComplete && 'border-divider bg-paper',
        isFailed && 'border-[#F2C8C8] bg-[#FFF9F9]',
      )}
    >
      <div
        className={clsx(
          'flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[10px]',
          isFailed ? 'bg-[#FFEAEA]' : 'bg-alt',
        )}
      >
        <FileTypeIcon mime={upload.file.mime} className="h-[18px] w-[18px] text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-[8px]">
          <p title={upload.file.name} className="truncate text-[13px] font-semibold leading-[normal] text-[#4A4A4A]">
            {upload.file.name}
          </p>
          <p className="shrink-0 rounded-[999px] bg-alt px-[7px] py-[2px] text-[10px] font-medium leading-[normal] text-[#757575]">{fileSizeLabel}</p>
        </div>
        {isInProgress ? (
          <div className="mt-[6px] flex items-center gap-[8px]">
            <div className="h-[8px] flex-1 overflow-hidden rounded-[999px] border border-primary bg-paper/80">
              <div
                className="h-full rounded-[999px] bg-primary transition-[width] duration-200"
                style={{width: `${uploadPercentage}%`}}
              />
            </div>
            <p className="w-[34px] shrink-0 text-right text-[11px] font-semibold leading-[normal] text-primary">{uploadPercentage}%</p>
          </div>
        ) : (
          <div className="mt-[6px] flex items-center gap-[6px]">
            <span className={clsx('h-[6px] w-[6px] rounded-full', isFailed ? 'bg-[#E34444]' : 'bg-primary')} />
            <p className={clsx('text-[11px] font-medium leading-[normal]', isFailed ? 'text-[#E34444]' : 'text-primary')}>
              {isFailed ? 'Upload failed' : 'Upload complete'}
            </p>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-end">
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            if (isInProgress) {
              abortUpload(upload.file.id);
            }
            removeUpload(upload.file.id);
          }}
          className={clsx(
            'inline-flex h-[30px] w-[30px] items-center justify-center rounded-full',
            'bg-[#D8562A] text-white',
          )}
          aria-label={`Remove ${upload.file.name}`}
        >
          <CloseIcon size="sm" />
        </button>
      </div>
    </div>
  );
}

function DriveSelectedFileListItem({
  entry,
  onRemove,
}: {
  entry: FileEntry;
  onRemove: () => void;
}) {
  const fileSizeLabel = formatDriveSize(entry.file_size);

  return (
    <div className="shrink-0 grid h-[74px] grid-cols-[32px,1fr,32px] items-center gap-[10px] rounded-[18px] border-2 border-divider bg-paper px-[10px] transition-colors">
      <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[10px] bg-alt">
        <FileTypeIcon
          type={mapDriveEntryToFileType({
            type: entry.type,
            extension: entry.extension,
            mime: entry.mime,
            id: entry.id,
            name: entry.name,
          })}
          className="h-[18px] w-[18px] text-primary"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-[8px]">
          <p
            title={entry.name}
            className="truncate text-[13px] font-semibold leading-[normal] text-[#4A4A4A]"
          >
            {entry.name}
          </p>
          <p className="shrink-0 rounded-[999px] bg-alt px-[7px] py-[2px] text-[10px] font-medium leading-[normal] text-[#757575]">
            {fileSizeLabel || 'Drive'}
          </p>
        </div>
        <div className="mt-[6px] flex items-center gap-[6px]">
          <span className="h-[6px] w-[6px] rounded-full bg-primary" />
          <p className="text-[11px] font-medium leading-[normal] text-primary">
            Added from Drive
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end">
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-alt text-[#d8562a]"
          aria-label={`Remove ${entry.name}`}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
